import moment from 'moment';
import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  addMemberToSpaceApi,
  advancedSearchApi,
  batchFetchPollsApi,
  closePollApi,
  createBookMarkApi,
  createCommentForPostApi,
  createDislikeForPostApi,
  createLikeDislikeCommentApi,
  createLikeForPostApi,
  createNewCircleUserApi,
  createNewSpaceApi,
  createReplyForACommentApi,
  createSpacePostApi,
  deleteBookMarkApi,
  deleteCirclePostApi,
  deleteCommentFromPostApi,
  deleteReplyFromCommentApi,
  deleteSpaceApi,
  directUploadApi,
  editCirclePostApi,
  editCommentApi,
  editReplyCommentApi,
  fetchBookMarkApi,
  fetchCurrentCommunityMember,
  fetchSpaceListApi,
  fetchSpaceListApiV2,
  fetchUserActivityApi,
  fetchUsersWithBadgesApi,
  fetchUsersSubscriptionStatusApi,
  getUsersStripeConnectionApi,
  flaggedContentApi,
  followCirclePostApi,
  getBookMarksApi,
  getCircleAuthToken,
  getCircleHomePageApi,
  getCommentListForPostApi,
  getListOfReplyForCommentApi,
  getMediaTranscodingStatusApi,
  getMemberDetailApi,
  getNotificationPrefernceApi,
  getNotificationsCountApi,
  getNotificationsListApi,
  getSinglePostApi,
  getSpaceDetailsApi,
  getSpaceMembersApi,
  getSpaceTopicDetailApi,
  getSpaceTopicsApi,
  groupPostSearchApi,
  getGroupHighlightsApi,
  pinGroupHighlightApi,
  unpinGroupHighlightApi,
  joinSpaceByIdApi,
  leaveSpaceByIdApi,
  markAllNotificationsAsReadApi,
  markNotificationAsReadApi,
  notificationPreferencesApi,
  onMultiMailApiCollection,
  removeSpaceMemberApi,
  resetNotificationCountApi,
  checkCircleMemberExists,
  trendingTopicsApi,
  onFetchSpaceMembeEmails,
  onGetModeratorFindCollection,
  onGetModeratorCreateAndUpdateCollection,
  submitPollVoteApi,
} from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { FEED_TAB, LOCAL_STORAGE_CIRCLE_AUTH_TOKEN } from '../../util/types';
import { UploadImage } from '../../util/cloudinary';
import { storableError } from '../../util/errors';
import { toastifyFn } from '../../components/ReactToastify/ReactToastify';
import {
  extractCirclePostIdsForPolls,
  getCirclePostIdForPoll,
  markPostsWithPollHydrationError,
  mergePollsIntoPosts,
  replacePollInPosts,
} from './components/Poll/pollFeedHydration';

const { UUID } = sdkTypes;
export const COMMUNITY_FEED_PAGE_SIZE = 10;
const resultIds = data => data?.data?.map?.(l => l.id);
const CIRCLE_MEMBER_EXISTS_CACHE_TTL_MS = 60 * 1000;
const circleMemberExistsCache = new Map();
const circleMemberExistsRequests = new Map();
const homePageRequests = new Map();
const usersBadgesRequests = new Set();
const usersSubscriptionRequests = new Set();
const usersStripeConnectionRequests = new Set();
let communityMemberRequest = null;
let notificationsListRequest = null;
let notificationsListRequestId = 0;
let notificationsCountRequest = null;

const normalizeCircleAuthParams = params => {
  if (typeof params === 'string') {
    return { email: params };
  }

  const { email, forceRefresh } = params || {};
  return {
    ...(email ? { email } : {}),
    ...(forceRefresh ? { forceRefresh } : {}),
  };
};

export const getCurrentUserCircleAuthParams = currentUser => {
  const { email, profile } = currentUser?.attributes || {};
  const { circleEmail } = profile?.privateData || {};

  return normalizeCircleAuthParams({
    email: circleEmail || email,
  });
};

const getCurrentUserCircleAuthToken = getState => {
  const currentUser = getState()?.user?.currentUser;
  return onGetCircleAuthToken(getCurrentUserCircleAuthParams(currentUser));
};

const getCircleMemberExistsCacheKey = communityMemberId =>
  `circle-member-exists:${communityMemberId}`;

const checkCircleMemberExistsCached = ({ accessToken, communityMemberId }) => {
  if (!communityMemberId) {
    return checkCircleMemberExists({ access_token: accessToken });
  }

  const cacheKey = getCircleMemberExistsCacheKey(communityMemberId);
  const cached = circleMemberExistsCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.value);
  }

  circleMemberExistsCache.delete(cacheKey);

  const inFlightRequest = circleMemberExistsRequests.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = checkCircleMemberExists({ access_token: accessToken })
    .then(response => {
      if (response?.id) {
        circleMemberExistsCache.set(cacheKey, {
          value: response,
          expiresAt: Date.now() + CIRCLE_MEMBER_EXISTS_CACHE_TTL_MS,
        });
      }
      return response;
    })
    .finally(() => {
      circleMemberExistsRequests.delete(cacheKey);
    });

  circleMemberExistsRequests.set(cacheKey, request);
  return request;
};

const groupBySpaceGroupName = data => {
  return data.reduce((acc, item) => {
    const groupName = item.space_group_name;
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(item);
    return acc;
  }, {});
};

export const SET_INITIAL_STATE = 'app/CirclePage/SET_INITIAL_STATE';
export const REQUEST_ACCESS_TOKEN = 'app/CirclePage/REQUEST_ACCESS_TOKEN';
export const SUCCESS_ACCESS_TOKEN = 'app/CirclePage/SUCCESS_ACCESS_TOKEN';
export const ERROR_ACCESS_TOKEN = 'app/CirclePage/ERROR_ACCESS_TOKEN';
export const REQUEST_HOME_PAGE = 'app/CirclePage/REQUEST_HOME_PAGE';
export const SUCCESS_HOME_PAGE = 'app/CirclePage/SUCCESS_HOME_PAGE';
export const ERROR_HOME_PAGE = 'app/CirclePage/ERROR_HOME_PAGE';
export const REQUEST_CREATE_POST = 'app/CirclePage/REQUEST_CREATE_POST';
export const SUCCESS_CREATE_POST = 'app/CirclePage/SUCCESS_CREATE_POST';
export const ERROR_CREATE_POST = 'app/CirclePage/ERROR_CREATE_POST';
export const REQUEST_LIKE_DISLIKE_POST = 'app/CirclePage/REQUEST_LIKE_DISLIKE_POST';
export const SUCCESS_LIKE_DISLIKE_POST = 'app/CirclePage/SUCCESS_LIKE_DISLIKE_POST';
export const ERROR_LIKE_DISLIKE_POST = 'app/CirclePage/ERROR_LIKE_DISLIKE_POST';
export const REQUEST_CREATE_COMMENT = 'app/CirclePage/REQUEST_CREATE_COMMENT';
export const SUCCESS_CREATE_COMMENT = 'app/CirclePage/SUCCESS_CREATE_COMMENT';
export const ERROR_CREATE_COMMENT = 'app/CirclePage/ERROR_CREATE_COMMENT';
export const REQUEST_DELETE_COMMENT = 'app/CirclePage/REQUEST_DELETE_COMMENT';
export const SUCCESS_DELETE_COMMENT = 'app/CirclePage/SUCCESS_DELETE_COMMENT';
export const ERROR_DELETE_COMMENT = 'app/CirclePage/ERROR_DELETE_COMMENT';
export const REQUEST_COMMUNITY_MEMBER = 'app/CirclePage/REQUEST_COMMUNITY_MEMBER';
export const SUCCESS_COMMUNITY_MEMBER = 'app/CirclePage/SUCCESS_COMMUNITY_MEMBER';
export const ERROR_COMMUNITY_MEMBER = 'app/CirclePage/ERROR_COMMUNITY_MEMBER';
export const REQUEST_SPACE_LIST = 'app/CirclePage/REQUEST_SPACE_LIST';
export const SUCCESS_SPACE_LIST = 'app/CirclePage/SUCCESS_SPACE_LIST';
export const ERROR_SPACE_LIST = 'app/CirclePage/ERROR_SPACE_LIST';
export const REQUEST_SPACE_LISTV2 = 'app/CirclePage/REQUEST_SPACE_LISTV2';
export const SUCCESS_SPACE_LISTV2 = 'app/CirclePage/SUCCESS_SPACE_LISTV2';
export const ERROR_SPACE_LISTV2 = 'app/CirclePage/ERROR_SPACE_LISTV2';
export const REQUEST_CREATE_USER = 'app/CirclePage/REQUEST_CREATE_USER';
export const SUCCESS_CREATE_USER = 'app/CirclePage/SUCCESS_CREATE_USER';
export const ERROR_CREATE_USER = 'app/CirclePage/ERROR_CREATE_USER';
export const REQUEST_DIRECT_UPLOAD = 'app/CirclePage/REQUEST_DIRECT_UPLOAD';
export const SUCCESS_DIRECT_UPLOAD = 'app/CirclePage/SUCCESS_DIRECT_UPLOAD';
export const ERROR_DIRECT_UPLOAD = 'app/CirclePage/ERROR_DIRECT_UPLOAD';
export const REQUEST_NEW_SPACE = 'app/CirclePage/REQUEST_NEW_SPACE';
export const SUCCESS_NEW_SPACE = 'app/CirclePage/SUCCESS_NEW_SPACE';
export const ERROR_NEW_SPACE = 'app/CirclePage/ERROR_NEW_SPACE';
export const REQUEST_JOIN_SPACE = 'app/CirclePage/REQUEST_JOIN_SPACE';
export const SUCCESS_JOIN_SPACE = 'app/CirclePage/SUCCESS_JOIN_SPACE';
export const ERROR_JOIN_SPACE = 'app/CirclePage/ERROR_JOIN_SPACE';
export const REQUEST_COMBINE_SPACE_TOPICS = 'app/CirclePage/REQUEST_COMBINE_SPACE_TOPICS';
export const SUCCESS_COMBINE_SPACE_TOPICS = 'app/CirclePage/SUCCESS_COMBINE_SPACE_TOPICS';
export const ERROR_COMBINE_SPACE_TOPICS = 'app/CirclePage/ERROR_COMBINE_SPACE_TOPICS';
export const REQUEST_LEAVE_SPACE = 'app/CirclePage/REQUEST_LEAVE_SPACE';
export const SUCCESS_LEAVE_SPACE = 'app/CirclePage/SUCCESS_LEAVE_SPACE';
export const ERROR_LEAVE_SPACE = 'app/CirclePage/ERROR_LEAVE_SPACE';
export const REQUEST_CREATE_BOOKMARK = 'app/CirclePage/REQUEST_CREATE_BOOKMARK';
export const SUCCESS_CREATE_BOOKMARK = 'app/CirclePage/SUCCESS_CREATE_BOOKMARK';
export const ERROR_CREATE_BOOKMARK = 'app/CirclePage/ERROR_CREATE_BOOKMARK';
export const REQUEST_DELETE_BOOKMARK = 'app/CirclePage/REQUEST_DELETE_BOOKMARK';
export const SUCCESS_DELETE_BOOKMARK = 'app/CirclePage/SUCCESS_DELETE_BOOKMARK';
export const ERROR_DELETE_BOOKMARK = 'app/CirclePage/ERROR_DELETE_BOOKMARK';
export const REQUEST_BOOKMARKS_LIST = 'app/CirclePage/REQUEST_BOOKMARKS_LIST';
export const SUCCESS_BOOKMARKS_LIST = 'app/CirclePage/SUCCESS_BOOKMARKS_LIST';
export const ERROR_BOOKMARKS_LIST = 'app/CirclePage/ERROR_BOOKMARKS_LIST';
export const REQUEST_SINGLE_POST = 'app/CirclePage/REQUEST_SINGLE_POST';
export const SUCCESS_SINGLE_POST = 'app/CirclePage/SUCCESS_SINGLE_POST';
export const ERROR_SINGLE_POST = 'app/CirclePage/ERROR_SINGLE_POST';
export const REQUEST_POST_COMMENT_LIST = 'app/CirclePage/REQUEST_POST_COMMENT_LIST';
export const SUCCESS_POST_COMMENT_LIST = 'app/CirclePage/SUCCESS_POST_COMMENT_LIST';
export const ERROR_POST_COMMENT_LIST = 'app/CirclePage/ERROR_POST_COMMENT_LIST';
export const REQUEST_LIKE_DISLIKE_COMMENT = 'app/CirclePage/REQUEST_LIKE_DISLIKE_COMMENT';
export const SUCCESS_LIKE_DISLIKE_COMMENT = 'app/CirclePage/SUCCESS_LIKE_DISLIKE_COMMENT';
export const ERROR_LIKE_DISLIKE_COMMENT = 'app/CirclePage/ERROR_LIKE_DISLIKE_COMMENT';
export const REQUEST_CREATE_REPLY_COMMENT = 'app/CirclePage/REQUEST_CREATE_REPLY_COMMENT';
export const SUCCESS_CREATE_REPLY_COMMENT = 'app/CirclePage/SUCCESS_CREATE_REPLY_COMMENT';
export const ERROR_CREATE_REPLY_COMMENT = 'app/CirclePage/ERROR_CREATE_REPLY_COMMENT';
export const REQUEST_SPACE_DETAILS = 'app/CirclePage/REQUEST_SPACE_DETAILS';
export const SUCCESS_SPACE_DETAILS = 'app/CirclePage/SUCCESS_SPACE_DETAILS';
export const ERROR_SPACE_DETAILS = 'app/CirclePage/ERROR_SPACE_DETAILS';
export const REQUEST_TOPIC_DETAILS = 'app/CirclePage/REQUEST_TOPIC_DETAILS';
export const SUCCESS_TOPIC_DETAILS = 'app/CirclePage/SUCCESS_TOPIC_DETAILS';
export const ERROR_TOPIC_DETAILS = 'app/CirclePage/ERROR_TOPIC_DETAILS';
export const REQUEST_SPACE_MEMBERS = 'app/CirclePage/REQUEST_SPACE_MEMBERS';
export const SUCCESS_SPACE_MEMBERS = 'app/CirclePage/SUCCESS_SPACE_MEMBERS';
export const SUCCESS_SPACE_MEMBER_FULL_LIST = 'app/CirclePage/SUCCESS_SPACE_MEMBER_FULL_LIST';
export const ERROR_SPACE_MEMBERS = 'app/CirclePage/ERROR_SPACE_MEMBERS';
export const REQUEST_REMOVE_SPACE_MEMBER = 'app/CirclePage/REQUEST_REMOVE_SPACE_MEMBER';
export const SUCCESS_REMOVE_SPACE_MEMBER = 'app/CirclePage/SUCCESS_REMOVE_SPACE_MEMBER';
export const ERROR_REMOVE_SPACE_MEMBER = 'app/CirclePage/ERROR_REMOVE_SPACE_MEMBER';
export const REQUEST_DELETE_SPACE = 'app/CirclePage/REQUEST_DELETE_SPACE';
export const SUCCESS_DELETE_SPACE = 'app/CirclePage/SUCCESS_DELETE_SPACE';
export const ERROR_DELETE_SPACE = 'app/CirclePage/ERROR_DELETE_SPACE';
export const REQUEST_ADVANCED_SEARCH = 'app/CirclePage/REQUEST_ADVANCED_SEARCH';
export const SUCCESS_ADVANCED_SEARCH = 'app/CirclePage/SUCCESS_ADVANCED_SEARCH';
export const ERROR_ADVANCED_SEARCH = 'app/CirclePage/ERROR_ADVANCED_SEARCH';
export const REQUEST_EDIT_COMMENT = 'app/CirclePage/REQUEST_EDIT_COMMENT';
export const SUCCESS_EDIT_COMMENT = 'app/CirclePage/SUCCESS_EDIT_COMMENT';
export const ERROR_EDIT_COMMENT = 'app/CirclePage/ERROR_EDIT_COMMENT';
export const REQUEST_DELETE_POST = 'app/CirclePage/REQUEST_DELETE_POST';
export const SUCCESS_DELETE_POST = 'app/CirclePage/SUCCESS_DELETE_POST';
export const ERROR_DELETE_POST = 'app/CirclePage/ERROR_DELETE_POST';
export const REQUEST_FOLLOW_POST = 'app/CirclePage/REQUEST_FOLLOW_POST';
export const SUCCESS_FOLLOW_POST = 'app/CirclePage/SUCCESS_FOLLOW_POST';
export const ERROR_FOLLOW_POST = 'app/CirclePage/ERROR_FOLLOW_POST';
export const REQUEST_FLAGGED_CONTENT = 'app/CirclePage/REQUEST_FLAGGED_CONTENT';
export const SUCCESS_FLAGGED_CONTENT = 'app/CirclePage/SUCCESS_FLAGGED_CONTENT';
export const ERROR_FLAGGED_CONTENT = 'app/CirclePage/ERROR_FLAGGED_CONTENT';
export const REQUEST_SPACE_TOPICS = 'app/CirclePage/REQUEST_SPACE_TOPICS';
export const SUCCESS_SPACE_TOPICS = 'app/CirclePage/SUCCESS_SPACE_TOPICS';
export const ERROR_SPACE_TOPICS = 'app/CirclePage/ERROR_SPACE_TOPICS';
export const CLEAR_SPACE_TOPICS = 'app/CirclePage/CLEAR_SPACE_TOPICS';
export const REQUEST_COMMENT_REPLIES = 'app/CirclePage/REQUEST_COMMENT_REPLIES';
export const SUCCESS_COMMENT_REPLIES = 'app/CirclePage/SUCCESS_COMMENT_REPLIES';
export const ERROR_COMMENT_REPLIES = 'app/CirclePage/ERROR_COMMENT_REPLIES';

export const TOGGEL_SEARCH_IN_GROUP = 'app/CirclePage/TOGGEL_SEARCH_IN_GROUP';
export const SET_ACTIVE_TAB = 'app/CirclePage/SET_ACTIVE_TAB';
export const TOGGLE_GIPHY = 'app/CirclePage/TOGGLE_GIPHY';
export const TOGGLE_DOCUMENT_MODAL = 'app/CirclePage/TOGGLE_DOCUMENT_MODAL';
export const TOGGLE_COVER_IMAGE_MODAL = 'app/CirclePage/TOGGLE_COVER_IMAGE_MODAL';
export const TOGGLE_IMAGE_MODAL = 'app/CirclePage/TOGGLE_IMAGE_MODAL';
export const TOGGLE_VIDEO_UPLOAD = 'app/CirclePage/TOGGLE_VIDEO_UPLOAD';
export const TOGGLE_REQUEST_SPACE = 'app/CirclePage/TOGGLE_REQUEST_SPACE';
export const TOGGLE_SEEALL_GROUP = 'app/CirclePage/TOGGLE_SEEALL_GROUP';
export const TOGGLE_EDIT_COMMENT = 'app/CirclePage/TOGGLE_EDIT_COMMENT';
export const TOGGLE_EMOJI = 'app/CirclePage/TOGGLE_EMOJI';
export const TOGGLE_CREATE_POST = 'app/CirclePage/TOGGLE_CREATE_POST';
export const ON_EDIT_POST = 'app/CirclePage/ON_EDIT_POST';
export const TOGGLE_REPORT_CONTENT = 'app/CirclePage/TOGGLE_REPORT_CONTENT';
export const TOGGLE_REPLY_GIPHY = 'app/CirclePage/TOGGLE_REPLY_';
export const TOGGLE_DOCUMENT_REPLY_MODAL = 'app/CirclePage/TOGGLE_DOCUMENT_REPLY_MODAL';
export const TOGGLE_IMAGE_REPLY_MODAL = 'app/CirclePage/TOGGLE_IMAGE_REPLY_MODAL';
export const TOGGLE_VIDEO_REPLY_UPLOAD = 'app/CirclePage/TOGGLE_VIDEO_REPLY_UPLOAD';
export const TOGGLE_REPLY_EMOJI = 'app/CirclePage/TOGGLE_REPLY_EMOJI';
export const REQUEST_NOTIFICATIONS = 'app/CirclePage/REQUEST_NOTIFICATIONS';
export const SUCCESS_NOTIFICATIONS = 'app/CirclePage/SUCCESS_NOTIFICATIONS';
export const ERROR_NOTIFICATIONS = 'app/CirclePage/ERROR_NOTIFICATIONS';
export const REQUEST_NOTIFICATION_COUNT = 'app/CirclePage/REQUEST_NOTIFICATION_COUNT';
export const SUCCESS_NOTIFICATION_COUNT = 'app/CirclePage/SUCCESS_NOTIFICATION_COUNT';
export const ERROR_NOTIFICATION_COUNT = 'app/CirclePage/ERROR_NOTIFICATION_COUNT';
export const REQUEST_MARK_AS_READ = 'app/CirclePage/REQUEST_MARK_AS_READ';
export const SUCCESS_MARK_AS_READ = 'app/CirclePage/SUCCESS_MARK_AS_READ';
export const ERROR_MARK_AS_READ = 'app/CirclePage/ERROR_MARK_AS_READ';
export const REQUEST_RESET_NOTIFICATIONS = 'app/CirclePage/REQUEST_RESET_NOTIFICATIONS';
export const SUCCESS_RESET_NOTIFICATIONS = 'app/CirclePage/SUCCESS_RESET_NOTIFICATIONS';
export const ERROR_RESET_NOTIFICATIONS = 'app/CirclePage/ERROR_RESET_NOTIFICATIONS';
export const REQUEST_MEMBER_DETAIL = 'app/CirclePage/REQUEST_MEMBER_DETAIL';
export const SUCCESS_MEMBER_DETAIL = 'app/CirclePage/SUCCESS_MEMBER_DETAIL';
export const ERROR_MEMBER_DETAIL = 'app/CirclePage/ERROR_MEMBER_DETAIL';
export const TOGGLE_IS_REPLY = 'app/CirclePage/TOGGLE_IS_REPLY';
export const TOGGLE_IS_ADD_COMMENT = 'app/CirclePage/TOGGLE_IS_ADD_COMMENT';
export const REQUEST_DELETE_REPLY = 'app/CirclePage/REQUEST_DELETE_REPLY';
export const SUCCESS_DELETE_REPLY = 'app/CirclePage/SUCCESS_DELETE_REPLY';
export const ERROR_DELETE_REPLY = 'app/CirclePage/ERROR_DELETE_REPLY';
export const REQUEST_ADD_MEMBER = 'app/CirclePage/REQUEST_ADD_MEMBER';
export const SUCCESS_ADD_MEMBER = 'app/CirclePage/SUCCESS_ADD_MEMBER';
export const ERROR_ADD_MEMBER = 'app/CirclePage/ERROR_ADD_MEMBER';
export const REQUEST_SPACE_MEMBER_FULL_LIST = 'app/CirclePage/REQUEST_SPACE_MEMBER_FULL_LIST';
export const REQUEST_MARKALL_NOTIFICATIONS_AS_READ =
  'app/CirclePage/REQUEST_MARKALL_NOTIFICATIONS_AS_READ';
export const SUCCESS_MARKALL_NOTIFICATIONS_AS_READ =
  'app/CirclePage/SUCCESS_MARKALL_NOTIFICATIONS_AS_READ';
export const ERROR_MARKALL_NOTIFICATIONS_AS_READ =
  'app/CirclePage/ERROR_MARKALL_NOTIFICATIONS_AS_READ';
export const REQUEST_NOTIFICATION_PREFERENCES = 'app/CirclePage/REQUEST_NOTIFICATION_PREFERENCES';
export const SUCCESS_NOTIFICATION_PREFERENCES = 'app/CirclePage/SUCCESS_NOTIFICATION_PREFERENCES';
export const ERROR_NOTIFICATION_PREFERENCES = 'app/CirclePage/ERROR_NOTIFICATION_PREFERENCES';
export const REQUEST_GROUP_POST_SEARCH = 'app/CirclePage/REQUEST_GROUP_POST_SEARCH';
export const SUCCESS_GROUP_POST_SEARCH = 'app/CirclePage/SUCCESS_GROUP_POST_SEARCH';
export const ERROR_GROUP_POST_SEARCH = 'app/CirclePage/ERROR_GROUP_POST_SEARCH';

export const FETCH_USER_ACTIVITY_REQUEST = 'app/CirclePage/FETCH_USER_ACTIVITY_REQUEST';
export const FETCH_USER_ACTIVITY_SUCCESS = 'app/CirclePage/FETCH_USER_ACTIVITY_SUCCESS';
export const FETCH_USER_ACTIVITY_ERROR = 'app/CirclePage/FETCH_USER_ACTIVITY_ERROR';

export const REQUEST_GROUP_HIGHLIGHTS = 'app/CirclePage/REQUEST_GROUP_HIGHLIGHTS';
export const SUCCESS_GROUP_HIGHLIGHTS = 'app/CirclePage/SUCCESS_GROUP_HIGHLIGHTS';
export const SUCCESS_GROUP_HIGHLIGHTS_STATUS = 'app/CirclePage/SUCCESS_GROUP_HIGHLIGHTS_STATUS';
export const ERROR_GROUP_HIGHLIGHTS = 'app/CirclePage/ERROR_GROUP_HIGHLIGHTS';
export const REQUEST_PIN_GROUP_HIGHLIGHT = 'app/CirclePage/REQUEST_PIN_GROUP_HIGHLIGHT';
export const SUCCESS_PIN_GROUP_HIGHLIGHT = 'app/CirclePage/SUCCESS_PIN_GROUP_HIGHLIGHT';
export const ERROR_PIN_GROUP_HIGHLIGHT = 'app/CirclePage/ERROR_PIN_GROUP_HIGHLIGHT';
export const SUCCESS_UNPIN_GROUP_HIGHLIGHT = 'app/CirclePage/SUCCESS_UNPIN_GROUP_HIGHLIGHT';
export const TOGGEL_DOCUMENT_DROPDOWN = 'app/CirclePage/TOGGEL_DOCUMENT_DROPDOWN';
export const TOGGEL_ADD_DOCUMENT = 'app/CirclePage/TOGGEL_ADD_DOCUMENT';
export const TOGGEL_ADD_FOLDER = 'app/CirclePage/TOGGEL_ADD_FOLDER';
export const TOGGEL_FOLDER_DOCUMENT = 'app/CirclePage/TOGGEL_FOLDER_DOCUMENT';

export const UPLOAD_DOCUMENT_REQUEST = 'app/CirclePage/UPLOAD_DOCUMENT_REQUEST';
export const UPLOAD_DOCUMENT_SUCCESS = 'app/CirclePage/UPLOAD_DOCUMENT_SUCCESS';
export const UPLOAD_DOCUMENT_ERROR = 'app/CirclePage/UPLOAD_DOCUMENT_ERROR';

// Badge action types
export const FETCH_USERS_BADGES_REQUEST = 'app/CirclePage/FETCH_USERS_BADGES_REQUEST';
export const FETCH_USERS_BADGES_SUCCESS = 'app/CirclePage/FETCH_USERS_BADGES_SUCCESS';
export const FETCH_USERS_BADGES_ERROR = 'app/CirclePage/FETCH_USERS_BADGES_ERROR';

// Subscription status action types
export const FETCH_USERS_SUBSCRIPTION_REQUEST = 'app/CirclePage/FETCH_USERS_SUBSCRIPTION_REQUEST';
export const FETCH_USERS_SUBSCRIPTION_SUCCESS = 'app/CirclePage/FETCH_USERS_SUBSCRIPTION_SUCCESS';
export const FETCH_USERS_SUBSCRIPTION_ERROR = 'app/CirclePage/FETCH_USERS_SUBSCRIPTION_ERROR';

// Stripe connection action types
export const FETCH_USERS_STRIPE_CONNECTION_REQUEST =
  'app/CirclePage/FETCH_USERS_STRIPE_CONNECTION_REQUEST';
export const FETCH_USERS_STRIPE_CONNECTION_SUCCESS =
  'app/CirclePage/FETCH_USERS_STRIPE_CONNECTION_SUCCESS';
export const FETCH_USERS_STRIPE_CONNECTION_ERROR =
  'app/CirclePage/FETCH_USERS_STRIPE_CONNECTION_ERROR';
export const REPLACE_POLL_BY_CIRCLE_POST_ID = 'app/CirclePage/REPLACE_POLL_BY_CIRCLE_POST_ID';
export const REQUEST_POLL_ACTION = 'app/CirclePage/REQUEST_POLL_ACTION';
export const SUCCESS_POLL_ACTION = 'app/CirclePage/SUCCESS_POLL_ACTION';
export const ERROR_POLL_ACTION = 'app/CirclePage/ERROR_POLL_ACTION';

const POLL_ACTION_VOTE = 'vote';
const POLL_ACTION_CLOSE = 'close';

const getRecordId = record => record?.id || record?.slug || record?.url;
const isSamePostId = (leftPostId, rightPostId) => String(leftPostId) === String(rightPostId);

const mergeUniqueRecords = (currentRecords = [], incomingRecords = []) => {
  const mergedRecords = [...currentRecords];
  const existingRecordIds = new Set(currentRecords.map(getRecordId).filter(Boolean));

  incomingRecords.forEach(record => {
    const recordId = getRecordId(record);

    if (!recordId || !existingRecordIds.has(recordId)) {
      mergedRecords.push(record);
    }

    if (recordId) {
      existingRecordIds.add(recordId);
    }
  });

  return mergedRecords;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const normalizePostRecord = record => {
  if (!record) {
    return record;
  }

  return hasOwn(record, 'is_pinned') && !hasOwn(record, 'is_pinned_at_top_of_space')
    ? { ...record, is_pinned_at_top_of_space: record.is_pinned }
    : record;
};

const getMediaAttachmentIdentity = attachment => {
  if (typeof attachment === 'string' || typeof attachment === 'number') {
    return String(attachment);
  }

  return (
    attachment?.signed_id ||
    attachment?.attachable_sgid ||
    attachment?.id ||
    attachment?.key ||
    attachment?.url ||
    attachment?.thq_media_url ||
    null
  );
};

const isRenderableMediaAttachment = attachment => {
  if (!attachment || typeof attachment !== 'object') {
    return false;
  }

  return Boolean(
    attachment.url ||
      attachment.thq_media_url ||
      Object.values(attachment.image_variants || {}).some(Boolean) ||
      Object.values(attachment.video_variants || {}).some(Boolean)
  );
};

const createAttachmentFromTiptapNode = node => {
  const attrs = node?.attrs || {};
  const url = attrs.url || attrs.src;
  const contentType =
    attrs.content_type ||
    attrs.contentType ||
    (typeof url === 'string' && /\.(gif)($|\?)/i.test(url)
      ? 'image/gif'
      : typeof url === 'string' && /\.(mp4|mov|webm|m4v)($|\?)/i.test(url)
      ? 'video/mp4'
      : node?.type === 'image'
      ? 'image/jpeg'
      : null);

  if (!url && !attrs.signed_id) {
    return null;
  }

  const isImage = contentType?.startsWith('image/');
  const isVideo = contentType?.startsWith('video/');

  return {
    ...attrs,
    id: attrs.signed_id || attrs.id || url,
    signed_id: attrs.signed_id || attrs.id,
    filename: attrs.filename || attrs.file_name || (contentType === 'image/gif' ? 'giphy.gif' : 'media'),
    content_type: contentType,
    url,
    image_variants: isImage
      ? {
          original: url,
          thumbnail: url,
          small: url,
          medium: url,
          large: url,
        }
      : attrs.image_variants,
    video_variants: isVideo
      ? {
          mp4: url,
        }
      : attrs.video_variants,
  };
};

const getTiptapBodyMediaAttachments = nodes => {
  if (!Array.isArray(nodes)) {
    return [];
  }

  return nodes.reduce((attachments, node) => {
    const nodeAttachment =
      node?.type === 'image' || node?.type === 'video' || node?.type === 'file'
        ? createAttachmentFromTiptapNode(node)
        : null;

    return [
      ...attachments,
      ...(nodeAttachment ? [nodeAttachment] : []),
      ...getTiptapBodyMediaAttachments(node?.content),
    ];
  }, []);
};

const mergeUniqueMediaAttachments = (...attachmentLists) => {
  const mergedAttachments = [];
  const attachmentIndexById = new Map();

  attachmentLists.forEach(attachmentList => {
    (Array.isArray(attachmentList) ? attachmentList : []).forEach(attachment => {
      if (!attachment) {
        return;
      }

      const attachmentId = getMediaAttachmentIdentity(attachment);

      if (!attachmentId) {
        mergedAttachments.push(attachment);
        return;
      }

      const existingIndex = attachmentIndexById.get(attachmentId);

      if (existingIndex === undefined) {
        attachmentIndexById.set(attachmentId, mergedAttachments.length);
        mergedAttachments.push(attachment);
        return;
      }

      if (
        !isRenderableMediaAttachment(mergedAttachments[existingIndex]) &&
        isRenderableMediaAttachment(attachment)
      ) {
        mergedAttachments[existingIndex] = attachment;
      }
    });
  });

  return mergedAttachments;
};

const normalizeTiptapMediaAttachments = (attachments = [], inlineAttachments = []) => {
  const normalizedAttachments = mergeUniqueMediaAttachments(attachments);
  const attachmentIndexById = new Map();

  normalizedAttachments.forEach((attachment, index) => {
    const attachmentId = getMediaAttachmentIdentity(attachment);
    if (attachmentId) {
      attachmentIndexById.set(attachmentId, index);
    }
  });

  const normalizedInlineAttachments = [];

  mergeUniqueMediaAttachments(inlineAttachments).forEach(attachment => {
    const attachmentId = getMediaAttachmentIdentity(attachment);
    const existingAttachmentIndex = attachmentId
      ? attachmentIndexById.get(attachmentId)
      : undefined;

    if (existingAttachmentIndex === undefined) {
      normalizedInlineAttachments.push(attachment);
      return;
    }

    if (
      !isRenderableMediaAttachment(normalizedAttachments[existingAttachmentIndex]) &&
      isRenderableMediaAttachment(attachment)
    ) {
      normalizedAttachments[existingAttachmentIndex] = attachment;
    }
  });

  return {
    attachments: normalizedAttachments,
    inline_attachments: normalizedInlineAttachments,
  };
};

const normalizeCommentTiptapMedia = tiptapBody => {
  if (!tiptapBody) {
    return tiptapBody;
  }

  const bodyMediaAttachments = getTiptapBodyMediaAttachments(tiptapBody.body?.content);
  const normalizedMedia = normalizeTiptapMediaAttachments(
    mergeUniqueMediaAttachments(bodyMediaAttachments, tiptapBody.attachments),
    tiptapBody.inline_attachments
  );

  return {
    ...tiptapBody,
    ...normalizedMedia,
  };
};

const mergeLocalMediaIntoComment = (comment, localMediaAttachments = []) => {
  if (!comment || !Array.isArray(localMediaAttachments) || !localMediaAttachments.length) {
    return comment;
  }

  const tiptapBody = comment.tiptap_body || {};
  const responseAttachments = Array.isArray(tiptapBody.attachments) ? tiptapBody.attachments : [];
  const responseInlineAttachments = Array.isArray(tiptapBody.inline_attachments)
    ? tiptapBody.inline_attachments
    : [];
  const normalizedMedia = normalizeTiptapMediaAttachments(
    mergeUniqueMediaAttachments(responseAttachments, localMediaAttachments),
    responseInlineAttachments
  );

  return {
    ...comment,
    tiptap_body: {
      ...tiptapBody,
      ...normalizedMedia,
    },
  };
};

const hasCommentMedia = comment => {
  const tiptapBody = comment?.tiptap_body || {};
  return Boolean(
    (Array.isArray(tiptapBody.attachments) && tiptapBody.attachments.length) ||
      (Array.isArray(tiptapBody.inline_attachments) && tiptapBody.inline_attachments.length)
  );
};

const mergeCommentRecord = (existingComment, updatedComment) => {
  if (!existingComment || !updatedComment) {
    return updatedComment || existingComment;
  }

  const shouldPreserveExistingMedia = hasCommentMedia(existingComment) && !hasCommentMedia(updatedComment);
  const existingTiptapBody = existingComment.tiptap_body || {};
  const updatedTiptapBody = updatedComment.tiptap_body || {};

  const mergedTiptapBody = shouldPreserveExistingMedia
    ? {
        ...existingTiptapBody,
        ...updatedTiptapBody,
        attachments: existingTiptapBody.attachments || [],
        inline_attachments: existingTiptapBody.inline_attachments || [],
        sgids_to_object_map:
          updatedTiptapBody.sgids_to_object_map || existingTiptapBody.sgids_to_object_map || {},
      }
    : updatedComment.tiptap_body || existingComment.tiptap_body;

  return {
    ...existingComment,
    ...updatedComment,
    tiptap_body: normalizeCommentTiptapMedia(mergedTiptapBody),
  };
};

const mergeCommentRecords = (existingRecords = [], incomingRecords = []) => {
  const existingById = new Map(existingRecords.map(record => [record?.id, record]));

  return incomingRecords.map(incomingRecord => {
    const existingRecord = existingById.get(incomingRecord?.id);
    const mergedRecord = mergeCommentRecord(existingRecord, incomingRecord);
    const existingReplies = existingRecord?.replies || [];
    const incomingReplies = incomingRecord?.replies || [];

    return {
      ...mergedRecord,
      replies: incomingReplies.length
        ? mergeCommentRecords(existingReplies, incomingReplies)
        : existingReplies.length
        ? existingReplies
        : incomingReplies,
    };
  });
};

const isPostPinnedAtTop = record => Boolean(record?.is_pinned_at_top_of_space || record?.is_pinned);

const sortPinnedPostsFirst = records =>
  Array.isArray(records)
    ? [...records].sort((firstRecord, secondRecord) => {
        const firstPinned = isPostPinnedAtTop(firstRecord);
        const secondPinned = isPostPinnedAtTop(secondRecord);

        if (firstPinned === secondPinned) {
          return 0;
        }

        return firstPinned ? -1 : 1;
      })
    : records;

const toggleBookmarkInPosts = (records = [], postId) =>
  records.map(item =>
    isSamePostId(item?.id, postId)
      ? {
          ...item,
          bookmark_id: !item.bookmark_id,
        }
      : item
  );

const removePostFromPosts = (records = [], postId) =>
  records.filter(record => !isSamePostId(record?.id, postId));

const mergePostRecord = (existingRecord, updatedRecord) => {
  if (!updatedRecord?.id) {
    return existingRecord || updatedRecord;
  }

  const normalizedUpdate = normalizePostRecord(updatedRecord);

  if (!existingRecord) {
    return normalizedUpdate;
  }

  const mergedRecord = {
    ...existingRecord,
    ...normalizedUpdate,
  };

  return existingRecord.poll && !hasOwn(normalizedUpdate, 'poll')
    ? { ...mergedRecord, poll: existingRecord.poll }
    : mergedRecord;
};

const replaceExistingRecord = (records = [], updatedRecord) =>
  updatedRecord?.id
    ? records.map(record =>
        isSamePostId(record?.id, updatedRecord.id) ? mergePostRecord(record, updatedRecord) : record
      )
    : records;

const omitObjectKey = (object = {}, key) => {
  const nextObject = { ...object };
  delete nextObject[key];
  return nextObject;
};

const hydratePollsForPosts = async posts => {
  const circlePostIds = extractCirclePostIdsForPolls(posts);

  if (!circlePostIds.length) {
    return Array.isArray(posts) ? posts : [];
  }

  try {
    const response = await batchFetchPollsApi({ circlePostIds });
    const polls = response?.success === false ? {} : response?.polls || {};
    return mergePollsIntoPosts(posts, polls);
  } catch (error) {
    console.warn('[pollHydration] Failed to hydrate polls for feed posts', {
      message: error?.message || error?.error || 'Unknown poll hydration error',
    });
    return markPostsWithPollHydrationError(posts);
  }
};

const hydratePollForPost = async post => {
  if (!post) {
    return post;
  }

  const [hydratedPost] = await hydratePollsForPosts([post]);
  return hydratedPost || post;
};

const replacePollInPost = (post, circlePostId, poll) =>
  post && getCirclePostIdForPoll(post) === String(circlePostId)
    ? {
        ...post,
        poll,
        pollHydrationError: null,
      }
    : post;

const getPollActionErrorMessage = error => {
  const status = error?.status || error?.statusCode;
  const errorCode = error?.error;

  if (status === 400 || errorCode === 'VALIDATION_ERROR') {
    return 'Choose a valid poll option and try again.';
  }
  if (status === 401 || errorCode === 'UNAUTHORIZED') {
    return 'Please sign in before voting in this poll.';
  }
  if (status === 403 || errorCode === 'FORBIDDEN') {
    return 'You do not have permission to do that.';
  }
  if (status === 404 || errorCode === 'NOT_FOUND') {
    return 'This poll could not be found. Please refresh and try again.';
  }
  if (status === 409 || errorCode === 'CONFLICT') {
    const message = String(error?.message || '').toLowerCase();
    if (message.includes('already voted')) {
      return 'You have already voted in this poll.';
    }
    if (message.includes('closed')) {
      return 'This poll is closed.';
    }
    return 'This poll was updated before your request completed. Please refresh and try again.';
  }

  return 'We could not update this poll. Please try again.';
};

const initialState = {
  isDropdownDocument: false,
  isAddDocument: false,
  isAddFolder: false,
  isFolderDocument: null,
  isSearchInModal: false,
  isSpaceMemberListRequested: false,
  isSpaceMemberListRequested: false,
  isReplyComment: null,
  isAddComment: false,
  activeTab: null,
  isGiphyOpen: false,
  isDocumentModal: false,
  isImageModal: false,
  isCoverImageModal: false,
  isVideoUpload: false,
  isRequestSpace: false,
  isSeeAllGroup: false,
  isEmojiOpen: false,
  isCreatePost: false,
  isReportModal: null,
  editPostData: null,
  editCommentData: null,
  accessToken: null,
  requestAccessToken: false,
  errorAccessToken: null,
  requestHomePage: false,
  loadMoreHomePage: false,
  homePageData: null,
  errorHomePage: null,
  requestCreatePost: false,
  createPostData: null,
  errorCreatePost: null,
  requestLikePost: false,
  likePostData: null,
  errorLikePost: null,
  requestCreateComment: false,
  createCommentData: null,
  errorCreateComment: null,
  requestDeleteComment: false,
  deleteCommentData: null,
  errorDeleteComment: null,
  currentCommunityMember: null,
  requestCommunityMember: false,
  errorCommunityMember: null,
  requestSpaceList: false,
  spaceListData: [],
  errorSpaceList: null,
  requestSpaceListV2: false,
  spaceListDataV2: [],
  errorSpaceListV2: null,
  requestCreateUser: false,
  circleNewUser: null,
  errorCreateUser: null,
  requestDirectUpload: false,
  directUploadData: null,
  errorDirectUpload: null,
  requestNewSpace: false,
  newSpaceData: null,
  errorNewSpace: null,
  requestJoinSpace: false,
  joinSpaceData: null,
  errorJoinSpace: null,
  requestCombineSpaceTopics: false,
  combineSpaceTopicsData: null,
  errorCombineSpaceTopics: null,
  requestLeaveSpace: false,
  leaveSpaceData: null,
  errorLeaveSpace: null,
  requestCreateBookmark: false,
  createBookmarkData: null,
  errorCreateBookmark: null,
  requestDeleteBookmark: false,
  deleteBookmarkData: null,
  errorDeleteBookmark: null,
  requestBookmarksList: false,
  bookmarksListData: null,
  errorBookmarksList: null,
  requestSinglePost: false,
  singlePostData: null,
  errorSinglePost: null,
  requestPostCommentList: false,
  loadMoreComments: false,
  postCommentListData: null,
  errorPostCommentList: null,
  requestLikeDislikeComment: false,
  likeDislikeCommentData: null,
  errorLikeDislikeComment: null,
  requestCreateReplyComment: false,
  createReplyCommentData: null,
  errorCreateReplyComment: null,
  requestSpaceDetails: false,
  spaceDetailsData: null,
  errorSpaceDetails: null,
  requestTopicDetails: false,
  topicDetailsData: null,
  errorTopicDetails: null,
  requestSpaceMembers: false,
  spaceMembersData: null,
  spaceMembersFullListData: null,
  errorSpaceMembers: null,
  requestRemoveSpaceMember: false,
  removeSpaceMemberData: null,
  errorRemoveSpaceMember: null,
  requestDeleteSpace: false,
  deleteSpaceData: null,
  errorDeleteSpace: null,
  requestAdvancedSearch: false,
  advancedSearchData: null,
  errorAdvancedSearch: null,
  requestEditComment: false,
  editCommentData: null,
  errorEditComment: null,
  requestDeletePost: null,
  errorDeletePost: null,
  requestFollowPost: null,
  errorFollowPost: null,
  requestFlaggedContent: false,
  flaggedContentData: null,
  errorFlaggedContent: null,
  isReplyGiphyOpen: false,
  isReplyDocumentModal: false,
  isReplyImageModal: false,
  isReplyVideoUpload: false,
  isReplyEmojiOpen: false,
  requestSpaceTopics: false,
  spaceTopicsData: null,
  errorSpaceTopics: null,
  requestNotificationsInProgress: false,
  notificationsData: null,
  errorNotifications: null,
  requestNotificationCountInProgress: false,
  notificationCountData: null,
  errorNotificationCount: null,
  requestMarkAsReadInProgress: false,
  markAsReadData: null,
  errorMarkAsRead: null,
  requestResetNotificationsInProgress: false,
  resetNotificationsData: null,
  errorResetNotifications: null,
  requestMemberDetail: false,
  memberDetailData: null,
  errorMemberDetail: null,
  loadCommentReplyInProgress: false,
  loadCommentReplyError: null,
  isDeleteReplyInProgress: false,
  deleteReplyData: null,
  isDeleteReplyError: null,
  requestMarkAllNotificationsAsRead: false,
  errorMarkAllNotificationsAsRead: null,
  requestNotificationPreferenceInProgress: false,
  requestChangeNotificationPreference: false,
  notificaitonPreferenceData: {},
  errorNotificationPreference: null,
  requestAddMemberInProgress: false,
  requestAddMemberError: null,
  alreadyAdded: null,
  groupPostSearchInProgress: false,
  groupPostSearchLoadMore: false,
  groupPostSearchError: null,
  groupPostSearchData: null,
  fetchUserActivityRequest: false,
  fetchUserActivityData: null,
  fetchUserActivityError: null,

  uploadInProgress: false,
  uploadedDocument: null,
  uploadError: null,

  // Badge state
  fetchUsersBadgesInProgress: false,
  usersBadgesMap: {},
  fetchUsersBadgesError: null,

  // Subscription status state
  fetchUsersSubscriptionInProgress: false,
  usersSubscriptionMap: {},
  fetchUsersSubscriptionError: null,

  // Stripe connection state
  fetchUsersStripeConnectionInProgress: false,
  usersStripeConnectionMap: {},
  fetchUsersStripeConnectionError: null,
  pollActionInProgressById: {},
  pollActionErrorById: {},

  requestGroupHighlights: false,
  groupHighlightsData: null,
  errorGroupHighlights: null,
  requestPinGroupHighlight: false,
  highlightsPostIds: [],
  highlightsPinnedAtByPostId: {},
};

export default function circlePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case TOGGEL_SEARCH_IN_GROUP:
      return { ...state, isSearchInModal: payload.data, groupPostSearchData: null };
    case REQUEST_SPACE_MEMBER_FULL_LIST:
      return { ...state, isSpaceMemberListRequested: payload.data };
    case TOGGLE_GIPHY:
      return { ...state, isGiphyOpen: !state.isGiphyOpen };
    case TOGGLE_DOCUMENT_MODAL:
      return { ...state, isDocumentModal: !state.isDocumentModal };
    case TOGGLE_IMAGE_MODAL:
      return { ...state, isImageModal: !state.isImageModal };
    case TOGGLE_COVER_IMAGE_MODAL:
      return { ...state, isCoverImageModal: !state.isCoverImageModal };
    case TOGGLE_VIDEO_UPLOAD:
      return { ...state, isVideoUpload: !state.isVideoUpload };
    case TOGGLE_REQUEST_SPACE:
      return { ...state, isRequestSpace: payload.data };
    case TOGGLE_SEEALL_GROUP:
      return { ...state, isSeeAllGroup: !state.isSeeAllGroup };
    case TOGGLE_EDIT_COMMENT:
      return { ...state, editCommentData: payload.data || null };
    case TOGGLE_EMOJI:
      return { ...state, isEmojiOpen: payload.data };
    case TOGGLE_CREATE_POST:
      return {
        ...state,
        isCreatePost:
          typeof payload?.data === 'boolean' ? payload.data : !state.isCreatePost,
      };
    case ON_EDIT_POST:
      return { ...state, editPostData: payload.data };
    case TOGGLE_REPORT_CONTENT:
      return { ...state, isReportModal: state.isReportModal ? null : payload };

    case TOGGLE_REPLY_GIPHY:
      return { ...state, isReplyGiphyOpen: !state.isReplyGiphyOpen };
    case TOGGLE_DOCUMENT_REPLY_MODAL:
      return { ...state, isReplyDocumentModal: !state.isReplyDocumentModal };
    case TOGGLE_IMAGE_REPLY_MODAL:
      return { ...state, isReplyImageModal: !state.isReplyImageModal };
    case TOGGLE_VIDEO_REPLY_UPLOAD:
      return { ...state, isReplyVideoUpload: !state.isReplyVideoUpload };
    case TOGGLE_REPLY_EMOJI:
      return { ...state, isReplyEmojiOpen: payload.data };
    case TOGGLE_IS_REPLY:
      return { ...state, isReplyComment: payload.data };
    case TOGGLE_IS_ADD_COMMENT:
      return { ...state, isAddComment: payload.data };

    case SET_INITIAL_STATE:
      return { ...initialState };
    case REQUEST_ACCESS_TOKEN:
      return { ...state, requestAccessToken: true };
    case SUCCESS_ACCESS_TOKEN:
      return { ...state, requestAccessToken: false, accessToken: payload.data };
    case ERROR_ACCESS_TOKEN:
      return {
        ...state,
        requestAccessToken: false,
        accessToken: null,
        errorAccessToken: payload.error,
      };
    case REQUEST_HOME_PAGE:
      return { ...state, requestHomePage: payload.page <= 1, loadMoreHomePage: payload.page > 1 };
    case SUCCESS_HOME_PAGE:
      const { data } = payload;
      const { page, records } = data;
      return {
        ...state,
        requestHomePage: false,
        loadMoreHomePage: false,
        homePageData: {
          ...data,
          records:
            page > 1
              ? mergeUniqueRecords(state.homePageData?.records || [], records || [])
              : records,
        },
      };

    case ERROR_HOME_PAGE:
      return {
        ...state,
        requestHomePage: false,
        loadMoreHomePage: false,
        errorHomePage: payload.error,
      };
    case REQUEST_CREATE_POST: {
      return { ...state, requestCreatePost: true, errorCreatePost: null };
    }
    case SUCCESS_CREATE_POST: {
      state.spaceDetailsData?.postResponse?.records;
      const newPost = normalizePostRecord(payload?.data);
      const existingHomeRecords = state.homePageData?.records || [];
      const updatedHomeRecords = existingHomeRecords.some(record =>
        isSamePostId(record.id, newPost?.id)
      )
        ? existingHomeRecords.map(record =>
            isSamePostId(record.id, newPost.id) ? mergePostRecord(record, newPost) : record
          )
        : newPost
        ? [newPost, ...existingHomeRecords]
        : existingHomeRecords;

      const existingSpaceRecords = state.spaceDetailsData?.postResponse?.records || [];
      const updatedSpaceRecords = existingSpaceRecords.some(record =>
        isSamePostId(record.id, newPost?.id)
      )
        ? existingSpaceRecords.map(record =>
            isSamePostId(record.id, newPost.id) ? mergePostRecord(record, newPost) : record
          )
        : newPost
        ? [newPost, ...existingSpaceRecords]
        : existingSpaceRecords;
      const updatedHighlightRecords = replaceExistingRecord(
        state.groupHighlightsData?.records || [],
        newPost
      );

      return {
        ...state,
        requestCreatePost: false,
        createPostData: newPost,
        errorCreatePost: null,
        homePageData: {
          ...(state.homePageData || {}),
          records: updatedHomeRecords,
        },
        spaceDetailsData: {
          ...(state.spaceDetailsData || {}),
          postResponse: {
            ...(state.spaceDetailsData?.postResponse || {}),
            records: sortPinnedPostsFirst(updatedSpaceRecords),
          },
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: sortPinnedPostsFirst(updatedHighlightRecords),
            }
          : state.groupHighlightsData,
      };
    }
    case REPLACE_POLL_BY_CIRCLE_POST_ID: {
      const { circlePostId, poll } = payload;
      return {
        ...state,
        homePageData: {
          ...(state.homePageData || {}),
          records: replacePollInPosts(state.homePageData?.records || [], circlePostId, poll),
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: replacePollInPosts(
                state.groupHighlightsData?.records || [],
                circlePostId,
                poll
              ),
            }
          : state.groupHighlightsData,
        spaceDetailsData: {
          ...(state.spaceDetailsData || {}),
          postResponse: {
            ...(state.spaceDetailsData?.postResponse || {}),
            records: replacePollInPosts(
              state.spaceDetailsData?.postResponse?.records || [],
              circlePostId,
              poll
            ),
          },
        },
        singlePostData: replacePollInPost(state.singlePostData, circlePostId, poll),
      };
    }
    case REQUEST_POLL_ACTION: {
      const { pollId, actionName } = payload;

      return {
        ...state,
        pollActionInProgressById: {
          ...(state.pollActionInProgressById || {}),
          [pollId]: actionName,
        },
        pollActionErrorById: omitObjectKey(state.pollActionErrorById, pollId),
      };
    }
    case SUCCESS_POLL_ACTION: {
      const { pollId, poll } = payload;
      const circlePostId = poll?.circlePostId;

      return {
        ...state,
        pollActionInProgressById: omitObjectKey(state.pollActionInProgressById, pollId),
        pollActionErrorById: omitObjectKey(state.pollActionErrorById, pollId),
        homePageData: {
          ...(state.homePageData || {}),
          records: circlePostId
            ? replacePollInPosts(state.homePageData?.records || [], circlePostId, poll)
            : state.homePageData?.records || [],
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: circlePostId
                ? replacePollInPosts(state.groupHighlightsData?.records || [], circlePostId, poll)
                : state.groupHighlightsData?.records || [],
            }
          : state.groupHighlightsData,
        spaceDetailsData: {
          ...(state.spaceDetailsData || {}),
          postResponse: {
            ...(state.spaceDetailsData?.postResponse || {}),
            records: circlePostId
              ? replacePollInPosts(
                  state.spaceDetailsData?.postResponse?.records || [],
                  circlePostId,
                  poll
                )
              : state.spaceDetailsData?.postResponse?.records || [],
          },
        },
        singlePostData: circlePostId
          ? replacePollInPost(state.singlePostData, circlePostId, poll)
          : state.singlePostData,
      };
    }
    case ERROR_POLL_ACTION: {
      const { pollId, message } = payload;

      return {
        ...state,
        pollActionInProgressById: omitObjectKey(state.pollActionInProgressById, pollId),
        pollActionErrorById: {
          ...(state.pollActionErrorById || {}),
          [pollId]: message,
        },
      };
    }
    case ERROR_CREATE_POST:
      return {
        ...state,
        requestCreatePost: false,
        createPostData: null,
        errorCreatePost: payload.error,
      };
    case REQUEST_LIKE_DISLIKE_POST:
      const updatedRecords =
        state?.homePageData?.records?.map(item =>
          item?.id === payload.postId
            ? {
                ...item,
                is_liked: !item.is_liked,
                user_likes_count: item.is_liked
                  ? item.user_likes_count - 1
                  : item.user_likes_count + 1,
              }
            : item
        ) || [];
      const updatedSpaceRecords =
        state?.spaceDetailsData?.postResponse?.records?.map(item =>
          item?.id === payload.postId
            ? {
                ...item,
                is_liked: !item.is_liked,
                user_likes_count: item.is_liked
                  ? item.user_likes_count - 1
                  : item.user_likes_count + 1,
              }
            : item
        ) || [];
      return {
        ...state,
        requestLikePost: true,
        homePageData: {
          ...state.homePageData,
          records: updatedRecords,
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state?.spaceDetailsData?.postResponse,
            records: updatedSpaceRecords,
          },
        },
      };
    case SUCCESS_LIKE_DISLIKE_POST:
      return { ...state, requestLikePost: false, likePostData: payload.data };
    case ERROR_LIKE_DISLIKE_POST:
      const revertedRecords =
        state?.homePageData?.records?.map(item =>
          item?.id === payload.postId
            ? {
                ...item,
                is_liked: !item.is_liked,
                user_likes_count: item.is_liked
                  ? item.user_likes_count - 1
                  : item.user_likes_count + 1,
              }
            : item
        ) || [];
      const revertSpaceRecords =
        state?.spaceDetailsData?.postResponse?.records?.map(item =>
          item?.id === payload.postId
            ? {
                ...item,
                is_liked: !item.is_liked,
                user_likes_count: item.is_liked
                  ? item.user_likes_count - 1
                  : item.user_likes_count + 1,
              }
            : item
        ) || [];
      return {
        ...state,
        requestLikePost: false,
        likePostData: null,
        errorLikePost: payload.error,
        homePageData: {
          ...state.homePageData,
          records: revertedRecords,
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state?.spaceDetailsData?.postResponse,
            records: revertSpaceRecords,
          },
        },
      };
    case REQUEST_CREATE_COMMENT:
      return { ...state, requestCreateComment: true };
    case SUCCESS_CREATE_COMMENT:
      const newComment = payload.data;
      const existingCommentIndex = state.postCommentListData.records.findIndex(
        item => item.id === newComment.id
      );
      return {
        ...state,
        requestCreateComment: false,
        createCommentData: payload.data,
        postCommentListData: {
          ...state.postCommentListData,
          records:
            existingCommentIndex !== -1
              ? state.postCommentListData.records.map((item, index) =>
                  index === existingCommentIndex ? mergeCommentRecord(item, newComment) : item
                )
              : [...state.postCommentListData.records, newComment],
        },
        homePageData: {
          ...state.homePageData,
          records: state?.homePageData?.records?.map(item =>
            item.id === payload.data.post_id
              ? {
                  ...item,
                  comment_count:
                    existingCommentIndex === -1 ? item.comment_count + 1 : item.comment_count,
                }
              : item
          ),
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state?.spaceDetailsData?.postResponse,
            records: state?.spaceDetailsData?.postResponse?.records?.map(item =>
              item.id === payload.data.post_id
                ? {
                    ...item,
                    comment_count:
                      existingCommentIndex === -1 ? item.comment_count + 1 : item.comment_count,
                  }
                : item
            ),
          },
        },
      };
    case ERROR_CREATE_COMMENT:
      return {
        ...state,
        requestCreateComment: false,
        createCommentData: null,
        errorCreateComment: payload.error,
      };
    case REQUEST_DELETE_COMMENT:
      return { ...state, requestDeleteComment: true };
    case SUCCESS_DELETE_COMMENT:
      return {
        ...state,
        requestDeleteComment: false,
        deleteCommentData: payload.data,
        postCommentListData: {
          ...state.postCommentListData,
          records: state.postCommentListData.records
            .filter(item => item.id !== payload.commentId) // Remove if it's a top-level comment
            .map(comment => ({
              ...comment,
              // Also remove from nested replies if it's a reply
              replies: comment.replies?.filter(reply => reply.id !== payload.commentId) || [],
              // Update replies_count if a reply was deleted
              replies_count: comment.replies?.some(reply => reply.id === payload.commentId)
                ? comment.replies_count - 1
                : comment.replies_count,
            })),
        },
        homePageData: {
          ...state.homePageData,
          records: state?.homePageData?.records?.map(item =>
            item.id === payload.commentId
              ? { ...item, comment_count: item.comment_count - 1 }
              : item
          ),
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state?.spaceDetailsData?.postResponse,
            records: state?.spaceDetailsData?.postResponse?.records?.map(item =>
              item.id === payload.commentId
                ? { ...item, comment_count: item.comment_count - 1 }
                : item
            ),
          },
        },
      };
    case ERROR_DELETE_COMMENT:
      return {
        ...state,
        requestDeleteComment: false,
        deleteCommentData: null,
        errorDeleteComment: payload.error,
      };
    case REQUEST_COMMUNITY_MEMBER:
      return { ...state, requestCommunityMember: true };
    case SUCCESS_COMMUNITY_MEMBER:
      return { ...state, requestCommunityMember: false, currentCommunityMember: payload.data };
    case ERROR_COMMUNITY_MEMBER:
      return {
        ...state,
        requestCommunityMember: false,
        currentCommunityMember: null,
        errorCommunityMember: payload.error,
      };
    case REQUEST_SPACE_LIST:
      return { ...state, requestSpaceList: true };
    case SUCCESS_SPACE_LIST:
      return { ...state, requestSpaceList: false, spaceListData: payload.data };
    case ERROR_SPACE_LIST:
      return {
        ...state,
        requestSpaceList: false,
        errorSpaceList: payload.error,
      };
    case REQUEST_SPACE_LISTV2:
      return { ...state, requestSpaceListV2: true };
    case SUCCESS_SPACE_LISTV2:
      return { ...state, requestSpaceListV2: false, spaceListDataV2: payload.data };
    case ERROR_SPACE_LISTV2:
      return {
        ...state,
        requestSpaceListV2: false,
        errorSpaceListV2: payload.error,
      };
    case REQUEST_CREATE_USER:
      return { ...state, requestCreateUser: true };
    case SUCCESS_CREATE_USER:
      return { ...state, requestCreateUser: false, circleNewUser: payload.data };
    case ERROR_CREATE_USER:
      return {
        ...state,
        requestCreateUser: false,
        circleNewUser: null,
        errorCreateUser: payload.error,
      };
    case REQUEST_DIRECT_UPLOAD:
      return { ...state, requestDirectUpload: true };
    case SUCCESS_DIRECT_UPLOAD:
      return { ...state, requestDirectUpload: false, directUploadData: payload.data };
    case ERROR_DIRECT_UPLOAD:
      return {
        ...state,
        requestDirectUpload: false,
        directUploadData: null,
        errorDirectUpload: payload.error,
      };
    case REQUEST_NEW_SPACE:
      return { ...state, requestNewSpace: true };
    case SUCCESS_NEW_SPACE:
      return { ...state, requestNewSpace: false, newSpaceData: payload.data };
    case ERROR_NEW_SPACE:
      return {
        ...state,
        requestNewSpace: false,
        newSpaceData: null,
        errorNewSpace: payload.error,
      };
    case REQUEST_JOIN_SPACE:
      return { ...state, requestJoinSpace: true };

    case SUCCESS_JOIN_SPACE:
      return {
        ...state,
        requestJoinSpace: false,
        joinSpaceData: payload.data,
        spaceListData: state.spaceListData.map(item =>
          item.id === payload.data.id ? { ...item, ...payload.data } : item
        ),
      };
    case ERROR_JOIN_SPACE:
      return {
        ...state,
        requestJoinSpace: false,
        joinSpaceData: null,
        errorJoinSpace: payload.error,
      };
    case REQUEST_COMBINE_SPACE_TOPICS:
      return { ...state, requestCombineSpaceTopics: true };
    case SUCCESS_COMBINE_SPACE_TOPICS:
      return { ...state, requestCombineSpaceTopics: false, combineSpaceTopicsData: payload.data };
    case ERROR_COMBINE_SPACE_TOPICS:
      return {
        ...state,
        requestCombineSpaceTopics: false,
        combineSpaceTopicsData: null,
        errorCombineSpaceTopics: payload.error,
      };
    case REQUEST_LEAVE_SPACE:
      return { ...state, requestLeaveSpace: true };
    case SUCCESS_LEAVE_SPACE:
      return {
        ...state,
        requestLeaveSpace: false,
        leaveSpaceData: payload.data,
        spaceListData: state.spaceListData.map(
          item =>
            item.id === payload.data.id
              ? { ...item, ...payload.data } // Replace with updated data
              : item // Keep the existing item
        ),
      };
    case ERROR_LEAVE_SPACE:
      return {
        ...state,
        requestLeaveSpace: false,
        leaveSpaceData: null,
        errorLeaveSpace: payload.error,
      };
    case REQUEST_CREATE_BOOKMARK:
      const createSpaceBookmarks = toggleBookmarkInPosts(
        state?.spaceDetailsData?.postResponse?.records || [],
        payload.postId
      );
      return {
        ...state,
        requestCreateBookmark: true,
        homePageData: {
          ...state.homePageData,
          records: toggleBookmarkInPosts(state?.homePageData?.records || [], payload.postId),
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state?.spaceDetailsData?.postResponse,
            records: createSpaceBookmarks,
          },
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: toggleBookmarkInPosts(
                state.groupHighlightsData?.records || [],
                payload.postId
              ),
            }
          : state.groupHighlightsData,
      };
    case SUCCESS_CREATE_BOOKMARK:
      return { ...state, requestCreateBookmark: false, createBookmarkData: payload.data };
    case ERROR_CREATE_BOOKMARK:
      return {
        ...state,
        requestCreateBookmark: false,
        createBookmarkData: null,
        errorCreateBookmark: payload.error,
        homePageData: {
          ...state.homePageData,
          records: toggleBookmarkInPosts(state?.homePageData?.records || [], payload.postId),
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: toggleBookmarkInPosts(
                state.groupHighlightsData?.records || [],
                payload.postId
              ),
            }
          : state.groupHighlightsData,
      };
    case REQUEST_DELETE_BOOKMARK:
      const deleteSpaceBookmarks = toggleBookmarkInPosts(
        state?.spaceDetailsData?.postResponse?.records || [],
        payload.postId
      );
      return {
        ...state,
        requestDeleteBookmark: true,
        homePageData: {
          ...state.homePageData,
          records: toggleBookmarkInPosts(state?.homePageData?.records || [], payload.postId),
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state?.spaceDetailsData?.postResponse,
            records: deleteSpaceBookmarks,
          },
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: toggleBookmarkInPosts(
                state.groupHighlightsData?.records || [],
                payload.postId
              ),
            }
          : state.groupHighlightsData,
      };
    case SUCCESS_DELETE_BOOKMARK:
      return { ...state, requestDeleteBookmark: false, deleteBookmarkData: payload.data };
    case ERROR_DELETE_BOOKMARK:
      return {
        ...state,
        requestDeleteBookmark: false,
        deleteBookmarkData: null,
        errorDeleteBookmark: payload.error,
        homePageData: {
          ...state.homePageData,
          records: toggleBookmarkInPosts(state?.homePageData?.records || [], payload.postId),
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: toggleBookmarkInPosts(
                state.groupHighlightsData?.records || [],
                payload.postId
              ),
            }
          : state.groupHighlightsData,
      };
    case REQUEST_BOOKMARKS_LIST:
      return { ...state, requestBookmarksList: true };
    case SUCCESS_BOOKMARKS_LIST:
      return { ...state, requestBookmarksList: false, bookmarksListData: payload.data };
    case ERROR_BOOKMARKS_LIST:
      return {
        ...state,
        requestBookmarksList: false,
        bookmarksListData: null,
        errorBookmarksList: payload.error,
      };
    case REQUEST_SINGLE_POST:
      return { ...state, requestSinglePost: true };
    case SUCCESS_SINGLE_POST:
      return {
        ...state,
        requestSinglePost: false,
        singlePostData: payload.data,
        // homePageData: {
        //   ...state.homePageData,
        //   records: state.homePageData.records.map(item =>
        //     resultIds({ data: [payload.data] }).includes(item.id)
        //       ? { ...item, ...payload.data }
        //       : item
        //   ),
        // },
        // bookmarksListData: {
        //   ...state.bookmarksListData,
        //   records: state.bookmarksListData.records.filter(
        //     item => item.record_id !== payload.data.id
        //   ),
        // },
      };
    case ERROR_SINGLE_POST:
      return {
        ...state,
        requestSinglePost: false,
        singlePostData: null,
        errorSinglePost: payload.error,
      };
    case REQUEST_POST_COMMENT_LIST:
      return {
        ...state,
        requestPostCommentList: payload.page === 1,
        loadMoreComments: payload.page > 1,
      };
    case SUCCESS_POST_COMMENT_LIST:
      return {
        ...state,
        requestPostCommentList: false,
        loadMoreComments: false,
        postCommentListData:
          payload.data.page > 1
            ? {
                ...state.postCommentListData,
                ...payload.data,
                records: [
                  ...mergeCommentRecords(
                    state.postCommentListData?.records || [],
                    state.postCommentListData?.records || []
                  ).map(oldItem => {
                    const incomingItem = (payload.data.records || []).find(
                      newItem => newItem.id === oldItem.id
                    );
                    return incomingItem ? mergeCommentRecord(oldItem, incomingItem) : oldItem;
                  }),
                  ...(payload.data.records || []).filter(
                    newItem =>
                      !state.postCommentListData?.records?.some(oldItem => oldItem.id === newItem.id)
                  ),
                ],
              }
            : {
                ...state.postCommentListData,
                ...payload.data,
                records: mergeCommentRecords(
                  state.postCommentListData?.records || [],
                  payload.data.records || []
                ),
              },
      };
    case ERROR_POST_COMMENT_LIST:
      return {
        ...state,
        requestPostCommentList: false,
        loadMoreComments: false,
        postCommentListData: null,
        errorPostCommentList: payload.error,
      };
    case REQUEST_LIKE_DISLIKE_COMMENT:
      return {
        ...state,
        requestLikeDislikeComment: true,
        postCommentListData: {
          ...state.postCommentListData,
          records: state.postCommentListData?.records.map(item =>
            item?.id === payload.commentId
              ? {
                  ...item,
                  is_liked: !item.is_liked,
                  user_likes_count: item.is_liked
                    ? item.user_likes_count - 1
                    : item.user_likes_count + 1,
                }
              : {
                  ...item,
                  replies: item?.replies?.map(reply =>
                    reply?.id === payload.commentId
                      ? {
                          ...reply,
                          is_liked: !reply.is_liked,
                          user_likes_count: reply.is_liked
                            ? reply.user_likes_count - 1
                            : reply.user_likes_count + 1,
                        }
                      : reply
                  ),
                }
          ),
        },
      };

    case SUCCESS_LIKE_DISLIKE_COMMENT:
      return { ...state, requestLikeDislikeComment: false, likeDislikeCommentData: payload.data };
    case ERROR_LIKE_DISLIKE_COMMENT:
      return {
        ...state,
        requestLikeDislikeComment: false,
        likeDislikeCommentData: null,
        errorLikeDislikeComment: payload.error,
      };
    case REQUEST_CREATE_REPLY_COMMENT:
      return { ...state, requestCreateReplyComment: true };
    case SUCCESS_CREATE_REPLY_COMMENT:
      return {
        ...state,
        requestCreateReplyComment: false,
        createReplyCommentData: payload.data,
        postCommentListData: {
          ...state.postCommentListData,
          records: state?.postCommentListData?.records?.map(item =>
            item.id === payload.data.parent_comment_id
              ? {
                  ...item,
                  replies: (item.replies || []).some(reply => reply.id === payload.data.id)
                    ? item.replies.map(reply =>
                        reply.id === payload.data.id ? mergeCommentRecord(reply, payload.data) : reply
                      )
                    : [...(item.replies || []), payload.data],
                }
              : item
          ),
        },
        homePageData: {
          ...state.homePageData,
          records: state?.homePageData?.records?.map(item =>
            item.id === payload.data.post_id
              ? { ...item, comment_count: item.comment_count + 1 }
              : item
          ),
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state?.spaceDetailsData?.postResponse,
            records: state?.spaceDetailsData?.postResponse?.records?.map(item =>
              item.id === payload.data.post_id
                ? { ...item, comment_count: item.comment_count + 1 }
                : item
            ),
          },
        },
      };
    case ERROR_CREATE_REPLY_COMMENT:
      return {
        ...state,
        requestCreateReplyComment: false,
        createReplyCommentData: null,
      };
    case REQUEST_SPACE_DETAILS:
      return {
        ...state,
        requestSpaceDetails: payload.page <= 1,
        loadMoreHomePage: payload.page > 1,
      };
    case SUCCESS_SPACE_DETAILS:
      return {
        ...state,
        requestSpaceDetails: false,
        loadMoreHomePage: false,
        spaceDetailsData: {
          ...payload.data,
          postResponse: {
            ...payload.data.postResponse,
            records:
              payload?.data?.postResponse?.page > 1
                ? sortPinnedPostsFirst(
                    mergeUniqueRecords(
                      state.spaceDetailsData?.postResponse?.records || [],
                      payload.data.postResponse?.records || []
                    )
                  )
                : sortPinnedPostsFirst(payload.data.postResponse?.records),
          },
        },
      };
    case ERROR_SPACE_DETAILS:
      return {
        ...state,
        requestSpaceDetails: false,
        loadMoreHomePage: false,
        // spaceDetailsData: null,
        errorSpaceDetails: payload.error,
      };
    case REQUEST_TOPIC_DETAILS:
      return { ...state, requestTopicDetails: true };
    case SUCCESS_TOPIC_DETAILS:
      return {
        ...state,
        requestTopicDetails: false,
        topicDetailsData: payload.data,
        spaceDetailsData: { ...state.spaceDetailsData, postResponse: payload.data },
      };
    case ERROR_TOPIC_DETAILS:
      return {
        ...state,
        requestTopicDetails: false,
        topicDetailsData: null,
        errorTopicDetails: payload.error,
      };
    case REQUEST_SPACE_MEMBERS:
      return { ...state, requestSpaceMembers: true };
    case SUCCESS_SPACE_MEMBERS:
      return { ...state, requestSpaceMembers: false, spaceMembersData: payload.data };
    case SUCCESS_SPACE_MEMBER_FULL_LIST:
      return { ...state, requestSpaceMembers: false, spaceMembersFullListData: payload.data };
    case ERROR_SPACE_MEMBERS:
      return {
        ...state,
        requestSpaceMembers: false,
        spaceMembersData: null,
        errorSpaceMembers: payload.error,
      };
    case REQUEST_REMOVE_SPACE_MEMBER:
      return { ...state, requestRemoveSpaceMember: true };
    case SUCCESS_REMOVE_SPACE_MEMBER:
      return {
        ...state,
        requestRemoveSpaceMember: false,
        removeSpaceMemberData: payload.data,
        spaceMembersData: {
          ...state.spaceMembersData,
          records: state.spaceMembersData?.records?.filter(
            member => member.community_member_id !== payload.community_member_id
          ),
        },
        spaceMembersFullListData: {
          ...state.spaceMembersFullListData,
          records: state.spaceMembersFullListData?.records?.filter(
            member => member.community_member_id !== payload.community_member_id
          ),
        },
      };
    case ERROR_REMOVE_SPACE_MEMBER:
      return {
        ...state,
        requestRemoveSpaceMember: false,
        removeSpaceMemberData: null,
        errorRemoveSpaceMember: payload.error,
      };
    case REQUEST_DELETE_SPACE:
      return { ...state, requestDeleteSpace: true };
    case SUCCESS_DELETE_SPACE:
      return { ...state, requestDeleteSpace: false, deleteSpaceData: payload.data };
    case ERROR_DELETE_SPACE:
      return {
        ...state,
        requestDeleteSpace: false,
        deleteSpaceData: null,
        errorDeleteSpace: payload.error,
      };
    case REQUEST_ADVANCED_SEARCH:
      return { ...state, requestAdvancedSearch: true };
    case SUCCESS_ADVANCED_SEARCH:
      return { ...state, requestAdvancedSearch: false, advancedSearchData: payload.data };
    case ERROR_ADVANCED_SEARCH:
      return {
        ...state,
        requestAdvancedSearch: false,
        advancedSearchData: null,
        errorAdvancedSearch: payload.error,
      };
    case REQUEST_EDIT_COMMENT:
      return { ...state, requestEditComment: true };
    case SUCCESS_EDIT_COMMENT:
      return {
        ...state,
        requestEditComment: false,
        editCommentData: mergeCommentRecord(state.editCommentData, payload.data),
        postCommentListData: {
          ...state.postCommentListData,
          records: state?.postCommentListData?.records?.map(item =>
            item.id === payload.data.id
              ? mergeCommentRecord(item, payload.data)
              : {
                  ...item,
                  replies: item?.replies?.map(reply =>
                    reply.id === payload.data.id ? mergeCommentRecord(reply, payload.data) : reply
                  ),
                }
          ),
        },
      };
    case ERROR_EDIT_COMMENT:
      return {
        ...state,
        requestEditComment: false,
        editCommentData: null,
        errorEditComment: payload.error,
      };

    case REQUEST_DELETE_POST:
      return { ...state, requestDeletePost: payload.postId };
    case SUCCESS_DELETE_POST:
      return {
        ...state,
        requestDeletePost: null,
        singlePostData:
          state?.singlePostData?.id === payload?.postId ? null : state?.singlePostData,
        homePageData: {
          ...state?.homePageData,
          records: removePostFromPosts(state?.homePageData?.records || [], payload?.postId),
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state.spaceDetailsData?.postResponse,
            records: removePostFromPosts(
              state.spaceDetailsData?.postResponse?.records || [],
              payload.postId
            ),
          },
        },
        groupHighlightsData: state.groupHighlightsData
          ? {
              ...state.groupHighlightsData,
              records: removePostFromPosts(
                state.groupHighlightsData?.records || [],
                payload.postId
              ),
              postIds: (state.groupHighlightsData?.postIds || []).filter(
                postId => !isSamePostId(postId, payload.postId)
              ),
            }
          : state.groupHighlightsData,
      };
    case ERROR_DELETE_POST:
      return {
        ...state,
        requestDeletePost: null,
        errorDeletePost: payload.error,
      };

    case REQUEST_FOLLOW_POST:
      return { ...state, requestDeletePost: payload.postId };
    case SUCCESS_FOLLOW_POST:
      return {
        ...state,
        requestDeletePost: null,
        homePageData: {
          ...state.homePageData,
          records: state.homePageData.records.some(record => record.id === payload.data.id)
            ? state.homePageData.records.map(record =>
                record.id === payload.data.id ? payload.data : record
              )
            : [payload.data, ...state.homePageData.records],
        },
        spaceDetailsData: {
          ...state.spaceDetailsData,
          postResponse: {
            ...state.spaceDetailsData?.postResponse,
            records: (state.spaceDetailsData?.postResponse?.records || []).some(
              record => record.id === payload.data.id
            )
              ? (state.spaceDetailsData?.postResponse?.records || []).map(record =>
                  record.id === payload.data.id ? payload.data : record
                )
              : [payload.data, ...(state.spaceDetailsData?.postResponse?.records || [])],
          },
        },
      };
    case ERROR_FOLLOW_POST:
      return {
        ...state,
        requestDeletePost: null,
        errorFollowPost: payload.error,
      };
    case REQUEST_FLAGGED_CONTENT:
      return { ...state, requestFlaggedContent: true };
    case SUCCESS_FLAGGED_CONTENT:
      return { ...state, requestFlaggedContent: false, flaggedContentData: payload.data };
    case ERROR_FLAGGED_CONTENT:
      return {
        ...state,
        requestFlaggedContent: false,
        flaggedContentData: null,
        errorFlaggedContent: payload.error,
      };
    case REQUEST_SPACE_TOPICS:
      return { ...state, requestSpaceTopics: true };
    case SUCCESS_SPACE_TOPICS:
      return { ...state, requestSpaceTopics: false, spaceTopicsData: payload.data };
    case ERROR_SPACE_TOPICS:
      return {
        ...state,
        requestSpaceTopics: false,
        spaceTopicsData: null,
        errorSpaceTopics: payload.error,
      };
    case CLEAR_SPACE_TOPICS: {
      return { ...state, spaceTopicsData: null };
    }
    case REQUEST_NOTIFICATIONS:
      return {
        ...state,
        requestNotificationsInProgress: true,
        errorNotifications: null,
        ...(payload?.clearData ? { notificationsData: null } : {}),
      };
    case SUCCESS_NOTIFICATIONS:
      return { ...state, requestNotificationsInProgress: false, notificationsData: payload.data };
    case ERROR_NOTIFICATIONS:
      return {
        ...state,
        requestNotificationsInProgress: false,
        notificationsData: null,
        errorNotifications: payload.error,
      };
    case REQUEST_NOTIFICATION_COUNT:
      return { ...state, requestNotificationCountInProgress: true };
    case SUCCESS_NOTIFICATION_COUNT:
      return {
        ...state,
        requestNotificationCountInProgress: false,
        notificationCountData: payload.data,
      };
    case ERROR_NOTIFICATION_COUNT:
      return {
        ...state,
        requestNotificationCountInProgress: false,
        notificationCountData: null,
        errorNotificationCount: payload.error,
      };
    case REQUEST_MARK_AS_READ:
      return { ...state, requestMarkAsReadInProgress: true };
    case SUCCESS_MARK_AS_READ:
      return {
        ...state,
        requestMarkAsReadInProgress: false,
        markAsReadData: payload.data,
        notificationsData: {
          ...state.notificationsData,
          records: state?.notificationsData?.records?.map(item =>
            item.id === payload.data.id ? payload.data : item
          ),
        },
      };
    case ERROR_MARK_AS_READ:
      return {
        ...state,
        requestMarkAsReadInProgress: false,
        markAsReadData: null,
        errorMarkAsRead: payload.error,
      };
    case SET_ACTIVE_TAB:
      return {
        ...state,
        activeTab: payload.tab,
      };
    case REQUEST_RESET_NOTIFICATIONS:
      return { ...state, requestResetNotificationsInProgress: true };
    case SUCCESS_RESET_NOTIFICATIONS:
      return {
        ...state,
        requestResetNotificationsInProgress: false,
        resetNotificationsData: payload.data,
        notificationCountData: {
          ...state.notificationCountData,
          new_notifications_count: payload.data.new_notifications_count,
        },
      };
    case ERROR_RESET_NOTIFICATIONS:
      return {
        ...state,
        requestResetNotificationsInProgress: false,
        resetNotificationsData: null,
        errorResetNotifications: payload.error,
      };
    case REQUEST_MEMBER_DETAIL:
      return { ...state, requestMemberDetail: true };
    case SUCCESS_MEMBER_DETAIL:
      return { ...state, requestMemberDetail: false, memberDetailData: payload.data };
    case ERROR_MEMBER_DETAIL:
      return {
        ...state,
        requestMemberDetail: false,
        memberDetailData: null,
        errorMemberDetail: payload.error,
      };
    case REQUEST_COMMENT_REPLIES:
      return { ...state, loadCommentReplyInProgress: true };
    case SUCCESS_COMMENT_REPLIES:
      return {
        ...state,
        loadCommentReplyInProgress: false,
        postCommentListData: {
          ...state.postCommentListData,
          records: state.postCommentListData?.records.map(item =>
            item?.id === payload.commentId
              ? {
                  ...item,
                  loadMoreReplies: payload.data,
                  replies: [
                    ...item.replies,
                    ...payload.data.records.filter(
                      newReply =>
                        !item.replies.some(existingReply => existingReply.id === newReply.id)
                    ),
                  ],
                }
              : item
          ),
        },
      };
    case ERROR_COMMENT_REPLIES:
      return { ...state, loadCommentReplyError: payload.error, loadCommentReplyInProgress: false };
    case REQUEST_MARKALL_NOTIFICATIONS_AS_READ:
      return { ...state, requestMarkAllNotificationsAsRead: true };
    case SUCCESS_MARKALL_NOTIFICATIONS_AS_READ:
      return { ...state, requestMarkAllNotificationsAsRead: false };
    case ERROR_MARKALL_NOTIFICATIONS_AS_READ:
      return {
        ...state,
        requestMarkAllNotificationsAsRead: false,
        errorMarkAllNotificationsAsRead: payload.error,
      };
    case REQUEST_NOTIFICATION_PREFERENCES:
      return {
        ...state,
        requestNotificationPreferenceInProgress: true,
        errorNotificationPreference: null,
      };
    case SUCCESS_NOTIFICATION_PREFERENCES:
      return {
        ...state,
        requestNotificationPreferenceInProgress: false,
        notificaitonPreferenceData: payload.data,
      };
    case ERROR_NOTIFICATION_PREFERENCES:
      return {
        ...state,
        requestNotificationPreferenceInProgress: false,
        errorNotificationPreference: payload.error,
      };

    case REQUEST_ADD_MEMBER:
      return { ...state, requestAddMemberInProgress: true, requestAddMemberError: null };
    case SUCCESS_ADD_MEMBER:
      return { ...state, requestAddMemberInProgress: false, alreadyAdded: payload };
    case ERROR_ADD_MEMBER:
      return { ...state, requestAddMemberInProgress: false, requestAddMemberError: payload.error };
    case REQUEST_GROUP_POST_SEARCH:
      return {
        ...state,
        groupPostSearchInProgress: payload.page <= 1,
        groupPostSearchLoadMore: payload.page > 1,
        groupPostSearchError: null,
      };
    case SUCCESS_GROUP_POST_SEARCH:
      return {
        ...state,
        groupPostSearchInProgress: false,
        groupPostSearchLoadMore: false,
        groupPostSearchData:
          payload.data.page > 1
            ? {
                ...state.groupPostSearchData,
                records: [...state.groupPostSearchData.records, ...payload.data.records],
              }
            : payload.data,
      };
    case ERROR_GROUP_POST_SEARCH:
      return {
        ...state,
        groupPostSearchInProgress: false,
        groupPostSearchLoadMore: false,
        groupPostSearchError: payload.error,
      };

    case FETCH_USER_ACTIVITY_REQUEST:
      return {
        ...state,
        fetchUserActivityRequest: true,
        fetchUserActivityData: null,
        fetchUserActivityError: null,
      };
    case FETCH_USER_ACTIVITY_SUCCESS:
      return {
        ...state,
        fetchUserActivityRequest: false,
        fetchUserActivityData: payload,
        fetchUserActivityError: null,
      };
    case FETCH_USER_ACTIVITY_ERROR:
      return {
        ...state,
        fetchUserActivityRequest: false,
        fetchUserActivityData: null,
        fetchUserActivityError: true,
      };

    case REQUEST_GROUP_HIGHLIGHTS:
      return { ...state, requestGroupHighlights: true, errorGroupHighlights: null };
    case SUCCESS_GROUP_HIGHLIGHTS:
      return {
        ...state,
        requestGroupHighlights: false,
        groupHighlightsData: payload.data,
        highlightsPostIds: payload.data?.postIds || [],
        highlightsPinnedAtByPostId: payload.data?.pinnedAtByPostId || {},
      };
    case SUCCESS_GROUP_HIGHLIGHTS_STATUS:
      return {
        ...state,
        highlightsPostIds: payload.data?.postIds || state.highlightsPostIds,
        highlightsPinnedAtByPostId: payload.data?.pinnedAtByPostId || {},
      };
    case ERROR_GROUP_HIGHLIGHTS:
      return {
        ...state,
        requestGroupHighlights: false,
        errorGroupHighlights: payload.error,
      };
    case REQUEST_PIN_GROUP_HIGHLIGHT:
      return { ...state, requestPinGroupHighlight: true };
    case SUCCESS_PIN_GROUP_HIGHLIGHT: {
      const pinnedPost = payload.post;
      const postId = payload.postId;
      const existingHighlightRecords = state.groupHighlightsData?.records || [];
      const updatedHighlightRecords = pinnedPost
        ? [pinnedPost, ...existingHighlightRecords.filter(r => !isSamePostId(r.id, postId))]
        : existingHighlightRecords;
      const updatedPostIds = state.highlightsPostIds.some(id => isSamePostId(id, postId))
        ? state.highlightsPostIds
        : [postId, ...state.highlightsPostIds];
      const updatedSpaceRecords =
        state.spaceDetailsData?.postResponse?.records?.map(item =>
          isSamePostId(item?.id, postId) ? { ...item, is_pinned_to_highlights: true } : item
        ) || [];
      return {
        ...state,
        requestPinGroupHighlight: false,
        highlightsPostIds: updatedPostIds,
        highlightsPinnedAtByPostId: {
          ...state.highlightsPinnedAtByPostId,
          [postId]: new Date().toISOString(),
        },
        groupHighlightsData: {
          ...(state.groupHighlightsData || {}),
          records: updatedHighlightRecords,
          postIds: updatedPostIds,
        },
        spaceDetailsData: state.spaceDetailsData
          ? {
              ...state.spaceDetailsData,
              postResponse: {
                ...state.spaceDetailsData.postResponse,
                records: updatedSpaceRecords,
              },
            }
          : state.spaceDetailsData,
      };
    }
    case SUCCESS_UNPIN_GROUP_HIGHLIGHT: {
      const postId = payload.postId;
      const updatedPostIds = state.highlightsPostIds.filter(id => !isSamePostId(id, postId));
      const { [postId]: _removed, ...restPinnedAt } = state.highlightsPinnedAtByPostId || {};
      const updatedSpaceRecords =
        state.spaceDetailsData?.postResponse?.records?.map(item =>
          isSamePostId(item?.id, postId) ? { ...item, is_pinned_to_highlights: false } : item
        ) || [];
      return {
        ...state,
        requestPinGroupHighlight: false,
        highlightsPostIds: updatedPostIds,
        highlightsPinnedAtByPostId: restPinnedAt,
        groupHighlightsData: {
          ...(state.groupHighlightsData || {}),
          records: (state.groupHighlightsData?.records || []).filter(
            r => !isSamePostId(r.id, postId)
          ),
          postIds: updatedPostIds,
        },
        spaceDetailsData: state.spaceDetailsData
          ? {
              ...state.spaceDetailsData,
              postResponse: {
                ...state.spaceDetailsData.postResponse,
                records: updatedSpaceRecords,
              },
            }
          : state.spaceDetailsData,
      };
    }
    case ERROR_PIN_GROUP_HIGHLIGHT:
      return { ...state, requestPinGroupHighlight: false };

    case TOGGEL_DOCUMENT_DROPDOWN:
      return {
        ...state,
        isDropdownDocument: payload.data,
      };
    case TOGGEL_ADD_DOCUMENT:
      return {
        ...state,
        isAddDocument: payload.data,
      };

    case UPLOAD_DOCUMENT_REQUEST:
      return {
        ...state,
        uploadInProgress: true,
      };
    case UPLOAD_DOCUMENT_SUCCESS:
      return {
        ...state,
        uploadInProgress: false,
        uploadedDocument: payload,
        uploadError: null,
      };
    case UPLOAD_DOCUMENT_ERROR:
      return {
        ...state,
        uploadInProgress: false,
        uploadedDocument: null,
        uploadError: payload,
      };

    case TOGGEL_ADD_FOLDER:
      return {
        ...state,
        isAddFolder: payload.data,
      };
    case TOGGEL_FOLDER_DOCUMENT:
      return {
        ...state,
        isFolderDocument: payload,
      };
    // Badge reducer cases
    case FETCH_USERS_BADGES_REQUEST:
      return {
        ...state,
        fetchUsersBadgesInProgress: true,
        fetchUsersBadgesError: null,
      };
    case FETCH_USERS_BADGES_SUCCESS:
      return {
        ...state,
        fetchUsersBadgesInProgress: false,
        usersBadgesMap: { ...state.usersBadgesMap, ...payload },
        fetchUsersBadgesError: null,
      };
    case FETCH_USERS_BADGES_ERROR:
      return {
        ...state,
        fetchUsersBadgesInProgress: false,
        fetchUsersBadgesError: payload,
      };
    // Subscription status reducer cases
    case FETCH_USERS_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        fetchUsersSubscriptionInProgress: true,
        fetchUsersSubscriptionError: null,
      };
    case FETCH_USERS_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        fetchUsersSubscriptionInProgress: false,
        usersSubscriptionMap: { ...state.usersSubscriptionMap, ...payload },
        fetchUsersSubscriptionError: null,
      };
    case FETCH_USERS_SUBSCRIPTION_ERROR:
      return {
        ...state,
        fetchUsersSubscriptionInProgress: false,
        fetchUsersSubscriptionError: payload,
      };
    // Stripe connection reducer cases
    case FETCH_USERS_STRIPE_CONNECTION_REQUEST:
      return {
        ...state,
        fetchUsersStripeConnectionInProgress: true,
        fetchUsersStripeConnectionError: null,
      };
    case FETCH_USERS_STRIPE_CONNECTION_SUCCESS:
      return {
        ...state,
        fetchUsersStripeConnectionInProgress: false,
        usersStripeConnectionMap: { ...state.usersStripeConnectionMap, ...payload },
        fetchUsersStripeConnectionError: null,
      };
    case FETCH_USERS_STRIPE_CONNECTION_ERROR:
      return {
        ...state,
        fetchUsersStripeConnectionInProgress: false,
        fetchUsersStripeConnectionError: payload,
      };
    default:
      return state;
  }
}

export const toggelDocumentDropdown = data => ({
  type: TOGGEL_DOCUMENT_DROPDOWN,
  payload: { data },
});

export const toggelAddDocument = data => ({
  type: TOGGEL_ADD_DOCUMENT,
  payload: { data },
});

export const toggelAddFolder = data => ({
  type: TOGGEL_ADD_FOLDER,
  payload: { data },
});

export const toggelFolderDocument = data => ({
  type: TOGGEL_FOLDER_DOCUMENT,
  payload: data,
});

export const toggelSearchInSpace = data => ({
  type: TOGGEL_SEARCH_IN_GROUP,
  payload: { data },
});

export const toggleSpaceMemberList = data => ({
  type: REQUEST_SPACE_MEMBER_FULL_LIST,
  payload: { data },
});

export const setActiveTab = tab => ({
  type: SET_ACTIVE_TAB,
  payload: { tab },
});

export const toggleGiphy = () => ({
  type: TOGGLE_GIPHY,
});

export const toggleDocumentModal = () => ({
  type: TOGGLE_DOCUMENT_MODAL,
});

export const toggleCoverImageModal = () => ({
  type: TOGGLE_COVER_IMAGE_MODAL,
});

export const toggleImageModal = () => ({
  type: TOGGLE_IMAGE_MODAL,
});

export const toggleVideoUpload = () => ({
  type: TOGGLE_VIDEO_UPLOAD,
});

export const toggleRequestSpace = data => ({
  type: TOGGLE_REQUEST_SPACE,
  payload: { data },
});

export const toggleSeeAllGroup = () => ({
  type: TOGGLE_SEEALL_GROUP,
});

export const toggelEditComment = data => ({
  type: TOGGLE_EDIT_COMMENT,
  payload: { data },
});

export const toggleEmoji = data => ({
  type: TOGGLE_EMOJI,
  payload: { data },
});

export const toggleReplyComment = data => ({
  type: TOGGLE_IS_REPLY,
  payload: { data },
});

export const toggleCreateComment = data => ({
  type: TOGGLE_IS_ADD_COMMENT,
  payload: { data },
});

export const toggleCreatePost = data => ({
  type: TOGGLE_CREATE_POST,
  payload: { data },
});

export const toggleEditPost = data => ({
  type: ON_EDIT_POST,
  payload: { data },
});

export const toggleReportContent = feedData => ({
  type: TOGGLE_REPORT_CONTENT,
  payload: feedData,
});

export const toggleReplyGiphy = () => ({
  type: TOGGLE_REPLY_GIPHY,
});

export const toggleReplyDocumentModal = () => ({
  type: TOGGLE_DOCUMENT_REPLY_MODAL,
});

export const toggleReplyImageModal = () => ({
  type: TOGGLE_IMAGE_REPLY_MODAL,
});

export const toggleReplyVideoUpload = () => ({
  type: TOGGLE_VIDEO_REPLY_UPLOAD,
});

export const toggleReplyEmoji = data => ({
  type: TOGGLE_REPLY_EMOJI,
  payload: { data },
});

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_STATE,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const requestAccessToken = () => ({
  type: REQUEST_ACCESS_TOKEN,
});
export const successAccessToken = data => ({
  type: SUCCESS_ACCESS_TOKEN,
  payload: { data },
});
export const errorAccessToken = error => ({
  type: ERROR_ACCESS_TOKEN,
  payload: { error },
});
export const requestHomePage = page => ({
  type: REQUEST_HOME_PAGE,
  payload: { page },
});
export const successHomePage = data => ({
  type: SUCCESS_HOME_PAGE,
  payload: { data },
});
export const errorHomePage = error => ({
  type: ERROR_HOME_PAGE,
  payload: { error },
});
export const requestCreatePost = () => ({
  type: REQUEST_CREATE_POST,
});
export const successCreatePost = data => ({
  type: SUCCESS_CREATE_POST,
  payload: { data },
});
export const errorCreatePost = error => ({
  type: ERROR_CREATE_POST,
  payload: { error },
});
export const replacePollByCirclePostId = (circlePostId, poll) => ({
  type: REPLACE_POLL_BY_CIRCLE_POST_ID,
  payload: { circlePostId, poll },
});
export const requestPollAction = (pollId, actionName) => ({
  type: REQUEST_POLL_ACTION,
  payload: { pollId, actionName },
});
export const successPollAction = (pollId, poll) => ({
  type: SUCCESS_POLL_ACTION,
  payload: { pollId, poll },
});
export const errorPollAction = (pollId, message) => ({
  type: ERROR_POLL_ACTION,
  payload: { pollId, message },
});
export const requestLikeDislikePost = postId => ({
  type: REQUEST_LIKE_DISLIKE_POST,
  payload: { postId },
});
export const successLikeDislikePost = data => ({
  type: SUCCESS_LIKE_DISLIKE_POST,
  payload: { data },
});
export const errorLikeDislikePost = (postId, error) => ({
  type: ERROR_LIKE_DISLIKE_POST,
  payload: { postId, error },
});

export const requestCreateComment = () => ({
  type: REQUEST_CREATE_COMMENT,
});
export const successCreateComment = data => ({
  type: SUCCESS_CREATE_COMMENT,
  payload: { data },
});
export const errorCreateComment = error => ({
  type: ERROR_CREATE_COMMENT,
  payload: { error },
});

export const requestDeleteComment = () => ({
  type: REQUEST_DELETE_COMMENT,
});
export const successDeleteComment = (data, commentId) => ({
  type: SUCCESS_DELETE_COMMENT,
  payload: { data, commentId },
});
export const errorDeleteComment = error => ({
  type: ERROR_DELETE_COMMENT,
  payload: { error },
});

export const requestCommunityMember = () => ({
  type: REQUEST_COMMUNITY_MEMBER,
});
export const successCommunityMember = data => ({
  type: SUCCESS_COMMUNITY_MEMBER,
  payload: { data },
});
export const errorCommunityMember = error => ({
  type: ERROR_COMMUNITY_MEMBER,
  payload: { error },
});
export const requestSpaceList = () => ({
  type: REQUEST_SPACE_LIST,
});
export const successSpaceList = data => ({
  type: SUCCESS_SPACE_LIST,
  payload: { data },
});
export const errorSpaceList = error => ({
  type: ERROR_SPACE_LIST,
  payload: { error },
});
export const requestSpaceListV2 = () => ({
  type: REQUEST_SPACE_LISTV2,
});
export const successSpaceListV2 = data => ({
  type: SUCCESS_SPACE_LISTV2,
  payload: { data },
});
export const errorSpaceListV2 = error => ({
  type: ERROR_SPACE_LISTV2,
  payload: { error },
});
export const requestCreateUser = () => ({
  type: REQUEST_CREATE_USER,
});
export const successCreateUser = data => ({
  type: SUCCESS_CREATE_USER,
  payload: { data },
});
export const errorCreateUser = error => ({
  type: ERROR_CREATE_USER,
  payload: { error },
});

export const requestDirectUpload = () => ({
  type: REQUEST_DIRECT_UPLOAD,
});
export const successDirectUpload = data => ({
  type: SUCCESS_DIRECT_UPLOAD,
  payload: { data },
});
export const errorDirectUpload = error => ({
  type: ERROR_DIRECT_UPLOAD,
  payload: { error },
});

export const requestNewSpace = () => ({
  type: REQUEST_NEW_SPACE,
});
export const successNewSpace = data => ({
  type: SUCCESS_NEW_SPACE,
  payload: { data },
});
export const errorNewSpace = error => ({
  type: ERROR_NEW_SPACE,
  payload: { error },
});

export const requestJoinSpace = () => ({
  type: REQUEST_JOIN_SPACE,
});
export const successJoinSpace = data => ({
  type: SUCCESS_JOIN_SPACE,
  payload: { data },
});
export const errorJoinSpace = error => ({
  type: ERROR_JOIN_SPACE,
  payload: { error },
});

export const requestCombineSpaceTopics = () => ({
  type: REQUEST_COMBINE_SPACE_TOPICS,
});
export const successCombineSpaceTopics = data => ({
  type: SUCCESS_COMBINE_SPACE_TOPICS,
  payload: { data },
});
export const errorCombineSpaceTopics = error => ({
  type: ERROR_COMBINE_SPACE_TOPICS,
  payload: { error },
});

export const requestLeaveSpace = () => ({
  type: REQUEST_LEAVE_SPACE,
});
export const successLeaveSpace = data => ({
  type: SUCCESS_LEAVE_SPACE,
  payload: { data },
});
export const errorLeaveSpace = error => ({
  type: ERROR_LEAVE_SPACE,
  payload: { error },
});

export const requestCreateBookmark = postId => ({
  type: REQUEST_CREATE_BOOKMARK,
  payload: { postId },
});
export const successCreateBookmark = data => ({
  type: SUCCESS_CREATE_BOOKMARK,
  payload: { data },
});
export const errorCreateBookmark = error => ({
  type: ERROR_CREATE_BOOKMARK,
  payload: { postId, error },
});
export const requestDeleteBookmark = postId => ({
  type: REQUEST_DELETE_BOOKMARK,
  payload: { postId },
});
export const successDeleteBookmark = data => ({
  type: SUCCESS_DELETE_BOOKMARK,
  payload: { data },
});
export const errorDeleteBookmark = (postId, error) => ({
  type: ERROR_DELETE_BOOKMARK,
  payload: { postId, error },
});

export const requestBookmarksList = () => ({
  type: REQUEST_BOOKMARKS_LIST,
});
export const successBookmarksList = data => ({
  type: SUCCESS_BOOKMARKS_LIST,
  payload: { data },
});
export const errorBookmarksList = error => ({
  type: ERROR_BOOKMARKS_LIST,
  payload: { error },
});

export const requestSinglePost = () => ({
  type: REQUEST_SINGLE_POST,
});
export const successSinglePost = data => ({
  type: SUCCESS_SINGLE_POST,
  payload: { data },
});
export const errorSinglePost = error => ({
  type: ERROR_SINGLE_POST,
  payload: { error },
});
export const requestPostCommentList = page => ({
  type: REQUEST_POST_COMMENT_LIST,
  payload: { page },
});
export const successPostCommentList = (data, postId) => ({
  type: SUCCESS_POST_COMMENT_LIST,
  payload: { data, postId },
});
export const errorPostCommentList = error => ({
  type: ERROR_POST_COMMENT_LIST,
  payload: { error },
});

export const requestLikeDislikeComment = commentId => ({
  type: REQUEST_LIKE_DISLIKE_COMMENT,
  payload: { commentId },
});
export const successLikeDislikeComment = data => ({
  type: SUCCESS_LIKE_DISLIKE_COMMENT,
  payload: { data },
});
export const errorLikeDislikeComment = (commentId, error) => ({
  type: ERROR_LIKE_DISLIKE_COMMENT,
  payload: { commentId, error },
});

export const requestCommentReply = () => ({
  type: REQUEST_CREATE_REPLY_COMMENT,
});
export const successCommentReply = data => ({
  type: SUCCESS_CREATE_REPLY_COMMENT,
  payload: { data },
});
export const errorCommentReply = error => ({
  type: ERROR_CREATE_REPLY_COMMENT,
  payload: { error },
});

export const requestSpaceDetails = page => ({
  type: REQUEST_SPACE_DETAILS,
  payload: { page },
});
export const successSpaceDetails = data => ({
  type: SUCCESS_SPACE_DETAILS,
  payload: { data },
});
export const errorSpaceDetails = error => ({
  type: ERROR_SPACE_DETAILS,
  payload: { error },
});

export const requestTopicDetails = () => ({
  type: REQUEST_TOPIC_DETAILS,
});
export const successTopicDetails = data => ({
  type: SUCCESS_TOPIC_DETAILS,
  payload: { data },
});
export const errorTopicDetails = error => ({
  type: ERROR_TOPIC_DETAILS,
  payload: { error },
});

export const requestSpaceMembersList = () => ({
  type: REQUEST_SPACE_MEMBERS,
});
export const successSpaceMembersList = data => ({
  type: SUCCESS_SPACE_MEMBERS,
  payload: { data },
});
export const successSpaceMemberFullList = data => ({
  type: SUCCESS_SPACE_MEMBER_FULL_LIST,
  payload: { data },
});
export const errorSpaceMembersList = error => ({
  type: ERROR_SPACE_MEMBERS,
  payload: { error },
});

export const requestRemoveSpaceMember = () => ({
  type: REQUEST_REMOVE_SPACE_MEMBER,
});
export const successRemoveSpaceMember = (data, community_member_id) => ({
  type: SUCCESS_REMOVE_SPACE_MEMBER,
  payload: { data, community_member_id },
});
export const errorRemoveSpaceMember = error => ({
  type: ERROR_REMOVE_SPACE_MEMBER,
  payload: { error },
});
export const requestDeleteSpace = () => ({
  type: REQUEST_DELETE_SPACE,
});
export const successDeleteSpace = data => ({
  type: SUCCESS_DELETE_SPACE,
  payload: { data },
});
export const errorDeleteSpace = error => ({
  type: ERROR_DELETE_SPACE,
  payload: { error },
});

export const requestAdvancedSearch = () => ({
  type: REQUEST_ADVANCED_SEARCH,
});
export const successAdvancedSearch = data => ({
  type: SUCCESS_ADVANCED_SEARCH,
  payload: { data },
});
export const errorAdvancedSearch = error => ({
  type: ERROR_ADVANCED_SEARCH,
  payload: { error },
});

export const requestEditComment = () => ({
  type: REQUEST_EDIT_COMMENT,
});
export const successEditComment = data => ({
  type: SUCCESS_EDIT_COMMENT,
  payload: { data },
});
export const errorEditComment = error => ({
  type: ERROR_EDIT_COMMENT,
  payload: { error },
});

export const requestDeletePost = postId => ({
  type: REQUEST_DELETE_POST,
  payload: { postId },
});
export const successDeletePost = postId => ({
  type: SUCCESS_DELETE_POST,
  payload: { postId },
});
export const errorDeletePost = error => ({
  type: ERROR_DELETE_POST,
  payload: { error },
});

export const requestFollowPost = postId => ({
  type: REQUEST_FOLLOW_POST,
  payload: { postId },
});

export const successFollowPost = data => ({
  type: SUCCESS_FOLLOW_POST,
  payload: { data },
});

export const errorFollowPost = (postId, error) => ({
  type: ERROR_FOLLOW_POST,
  payload: { postId, error },
});

export const requestFlaggedContent = () => ({
  type: REQUEST_FLAGGED_CONTENT,
});
export const successFlaggedContent = data => ({
  type: SUCCESS_FLAGGED_CONTENT,
  payload: { data },
});
export const errorFlaggedContent = error => ({
  type: ERROR_FLAGGED_CONTENT,
  payload: { error },
});

export const requestSpaceTopics = () => ({
  type: REQUEST_SPACE_TOPICS,
});
export const successSpaceTopics = data => ({
  type: SUCCESS_SPACE_TOPICS,
  payload: { data },
});
export const errorSpaceTopics = error => ({
  type: ERROR_SPACE_TOPICS,
  payload: { error },
});
export const clearSpaceTopics = () => ({
  type: CLEAR_SPACE_TOPICS,
});

export const requestNotifications = (params = {}) => ({
  type: REQUEST_NOTIFICATIONS,
  payload: params,
});
export const successNotifications = data => ({
  type: SUCCESS_NOTIFICATIONS,
  payload: { data },
});
export const errorNotifications = error => ({
  type: ERROR_NOTIFICATIONS,
  payload: { error },
});

export const requestNotificationCount = () => ({
  type: REQUEST_NOTIFICATION_COUNT,
});
export const successNotificationCount = data => ({
  type: SUCCESS_NOTIFICATION_COUNT,
  payload: { data },
});
export const errorNotificationCount = error => ({
  type: ERROR_NOTIFICATION_COUNT,
  payload: { error },
});

export const requestMarkAsRead = () => ({
  type: REQUEST_MARK_AS_READ,
});
export const successMarkAsRead = data => ({
  type: SUCCESS_MARK_AS_READ,
  payload: { data },
});
export const errorMarkAsRead = error => ({
  type: ERROR_MARK_AS_READ,
  payload: { error },
});

export const requestResetNotifications = () => ({
  type: REQUEST_RESET_NOTIFICATIONS,
});
export const successResetNotifications = data => ({
  type: SUCCESS_RESET_NOTIFICATIONS,
  payload: { data },
});
export const errorResetNotifications = error => ({
  type: ERROR_RESET_NOTIFICATIONS,
  payload: { error },
});

export const toggelGiphyReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleGiphy());
};

export const toggelDocumentReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleDocumentModal());
};

export const toggelCoverImageReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleCoverImageModal());
};

export const toggelImageReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleImageModal());
};

export const toggelVideoReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleVideoUpload());
};
export const toggelReplyGiphyReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleReplyGiphy());
};

export const toggelReplyDocumentReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleReplyDocumentModal());
};

export const toggelReplyImageReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleReplyImageModal());
};

export const toggelReplyVideoReducer = () => (dispatch, getState, sdk) => {
  dispatch(toggleReplyVideoUpload());
};

export const toggleReplyEmojiReducer = params => (dispatch, getState, sdk) => {
  dispatch(toggleReplyEmoji(params));
};

export const toggelRequestSpaceReducer = param => (dispatch, getState, sdk) => {
  dispatch(toggleRequestSpace(param));
  // Removed fetchSpaceList() - space list is already loaded on page init, no need to refetch on modal toggle
};

export const requestMemberDetail = () => ({
  type: REQUEST_MEMBER_DETAIL,
});
export const successMemberDetail = data => ({
  type: SUCCESS_MEMBER_DETAIL,
  payload: { data },
});
export const errorMemberDetail = error => ({
  type: ERROR_MEMBER_DETAIL,
  payload: { error },
});

export const requestCommentReplyList = () => ({
  type: REQUEST_COMMENT_REPLIES,
});

export const successCommentReplyList = (data, commentId) => ({
  type: SUCCESS_COMMENT_REPLIES,
  payload: { data, commentId },
});

export const errorCommentReplyList = error => ({
  type: ERROR_COMMENT_REPLIES,
  payload: { error },
});

export const requestMarkAllNotification = () => ({
  type: REQUEST_MARKALL_NOTIFICATIONS_AS_READ,
});

export const successMarkAllNotification = data => ({
  type: SUCCESS_MARKALL_NOTIFICATIONS_AS_READ,
  payload: { data },
});

export const errorMarkAllNotification = error => ({
  type: ERROR_MARKALL_NOTIFICATIONS_AS_READ,
  payload: { error },
});

export const requestNotificationPreference = () => ({
  type: REQUEST_NOTIFICATION_PREFERENCES,
});

export const successNotificationPreference = data => ({
  type: SUCCESS_NOTIFICATION_PREFERENCES,
  payload: { data },
});

export const errorNotificationPreference = error => ({
  type: ERROR_NOTIFICATION_PREFERENCES,
  payload: { error },
});

export const fetchUserActivityRequest = () => ({
  type: FETCH_USER_ACTIVITY_REQUEST,
});

export const fetchUserActivitySuccess = data => ({
  type: FETCH_USER_ACTIVITY_SUCCESS,
  payload: data,
});

export const fetchUserActivityError = error => ({
  type: FETCH_USER_ACTIVITY_ERROR,
  payload: error,
});

export const requestAddMember = () => ({
  type: REQUEST_ADD_MEMBER,
});

export const successAddMember = response => ({
  type: SUCCESS_ADD_MEMBER,
  payload: response,
});

export const errorAddMember = error => ({
  type: ERROR_ADD_MEMBER,
  payload: { error },
});

export const requestGroupPostSearch = page => ({
  type: REQUEST_GROUP_POST_SEARCH,
  payload: { page },
});

export const successGroupPostSearch = data => ({
  type: SUCCESS_GROUP_POST_SEARCH,
  payload: { data },
});

export const errorGroupPostSearch = error => ({
  type: ERROR_GROUP_POST_SEARCH,
  payload: { error },
});

export const requestGroupHighlights = () => ({ type: REQUEST_GROUP_HIGHLIGHTS });
export const successGroupHighlights = data => ({
  type: SUCCESS_GROUP_HIGHLIGHTS,
  payload: { data },
});
export const successGroupHighlightsStatus = data => ({
  type: SUCCESS_GROUP_HIGHLIGHTS_STATUS,
  payload: { data },
});
export const errorGroupHighlights = error => ({
  type: ERROR_GROUP_HIGHLIGHTS,
  payload: { error },
});
export const requestPinGroupHighlight = () => ({ type: REQUEST_PIN_GROUP_HIGHLIGHT });
export const successPinGroupHighlight = (post, postId) => ({
  type: SUCCESS_PIN_GROUP_HIGHLIGHT,
  payload: { post, postId },
});
export const successUnpinGroupHighlight = postId => ({
  type: SUCCESS_UNPIN_GROUP_HIGHLIGHT,
  payload: { postId },
});
export const errorPinGroupHighlight = error => ({
  type: ERROR_PIN_GROUP_HIGHLIGHT,
  payload: { error },
});

export const requestUploadDocument = () => ({
  type: UPLOAD_DOCUMENT_REQUEST,
});

export const successUploadDocument = response => ({
  type: UPLOAD_DOCUMENT_SUCCESS,
  payload: response,
});

export const errorUploadDocument = error => ({
  type: UPLOAD_DOCUMENT_ERROR,
  payload: { error },
});

export const setInitalLoadToken = async params => {
  try {
    if (typeof window === 'undefined') return {};
    const newToken = await getCircleAuthToken(normalizeCircleAuthParams(params));
    if (newToken) {
      localStorage.setItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN, JSON.stringify(newToken));
      return newToken;
    } else {
      localStorage.removeItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN);
      return {};
    }
  } catch (e) {
    return {};
  }
};

export const onGetCircleAuthToken = async params => {
  if (typeof window === 'undefined') return {};
  const authParams = normalizeCircleAuthParams(params);
  const forceRefresh = authParams.forceRefresh;

  // Retrieve the stored token from localStorage
  const storedToken = localStorage.getItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN);
  const tokenData = (() => {
    try {
      return storedToken ? JSON.parse(storedToken) : {};
    } catch (e) {
      localStorage.removeItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN);
      return {};
    }
  })();

  const {
    access_token,
    access_token_expires_at,
    community_member_id,
    success,
    message,
  } = tokenData;
  // Check if the access token exists and is expired
  const isTokenExpired =
    access_token_expires_at && moment(access_token_expires_at).isBefore(moment());

  if (!forceRefresh && access_token && !isTokenExpired) {
    //check if member is deleted from circle directly
    const response = await checkCircleMemberExistsCached({
      accessToken: access_token,
      communityMemberId: community_member_id,
    });
    if (!response?.id) {
      return response;
    }
  }

  if (forceRefresh || !access_token || !community_member_id || isTokenExpired) {
    // Request a new token if the current one is invalid or expired
    const newToken = await getCircleAuthToken(authParams);
    if (newToken) {
      localStorage.setItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN, JSON.stringify(newToken));
    }
    return newToken;
  }

  // Return the existing valid token
  return {
    access_token,
    community_member_id,
    success,
    message,
  };
};

export const fetchCircleAuthToken = params => async (dispatch, getState, sdk) => {
  try {
    const { page = 1, forceRefreshSpaceList = false } = params || {};
    dispatch(requestAccessToken());

    const currentUser = getState()?.user?.currentUser;
    const authParams = getCurrentUserCircleAuthParams(currentUser);
    const { access_token, community_member_id, success, message } = await onGetCircleAuthToken(
      authParams
    );
    dispatch(successAccessToken(access_token ?? { success, message }));

    if (access_token && community_member_id) {
      const { spaceListData, requestSpaceList } = getState().CircleCommunityPage || {};
      const { topbarGroups, topbarGroupsRequest } = getState().user || {};
      const shouldUseExistingSpaceList =
        !forceRefreshSpaceList && Array.isArray(spaceListData) && spaceListData.length > 0;
      const shouldUseTopbarSpaceList =
        !forceRefreshSpaceList && Array.isArray(topbarGroups) && topbarGroups.length > 0;

      if (!shouldUseExistingSpaceList && shouldUseTopbarSpaceList) {
        dispatch(successSpaceList(topbarGroups));
      }

      const shouldFetchSpaceList =
        !shouldUseExistingSpaceList &&
        !shouldUseTopbarSpaceList &&
        (forceRefreshSpaceList || (!requestSpaceList && !topbarGroupsRequest));

      dispatch(setActiveTab(FEED_TAB));
      return Promise.all([
        dispatch(fetchHomePage({ access_token, page })),
        dispatch(fetchCommunityMember(access_token, community_member_id)),
        shouldFetchSpaceList
          ? dispatch(fetchSpaceList({ forceRefresh: forceRefreshSpaceList }))
          : Promise.resolve(spaceListData || topbarGroups || []),
      ]);
    }
  } catch (error) {
    dispatch(errorAccessToken(error));
  }
};

export const fetchCommunityMember = () => async (dispatch, getState, sdk) => {
  const { currentCommunityMember } = getState().CircleCommunityPage || {};

  if (currentCommunityMember) {
    return currentCommunityMember;
  }

  if (communityMemberRequest) {
    return communityMemberRequest;
  }

  try {
    dispatch(requestCommunityMember());

    communityMemberRequest = (async () => {
      const currentUser = getState()?.user?.currentUser;
      const authParams = getCurrentUserCircleAuthParams(currentUser);
      const { access_token, community_member_id, success, message } = await onGetCircleAuthToken(
        authParams
      );

      dispatch(successAccessToken(access_token ?? { success, message }));

      return fetchCurrentCommunityMember({
        accessToken: access_token,
        community_member_id,
      });
    })();

    const memberResponse = await communityMemberRequest;
    dispatch(successCommunityMember(memberResponse));
    return memberResponse;
  } catch (error) {
    dispatch(errorCommunityMember(error));
  } finally {
    communityMemberRequest = null;
  }
};

export const fetchHomePage = params => async (dispatch, getState, sdk) => {
  const { page = 1, sort = 'latest', per_page = COMMUNITY_FEED_PAGE_SIZE } = params;

  try {
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const requestKey = JSON.stringify({ access_token, page, sort, per_page });

    if (homePageRequests.has(requestKey)) {
      return homePageRequests.get(requestKey);
    }

    const request = (async () => {
      dispatch(requestHomePage(page));
      const response = await getCircleHomePageApi({
        accessToken: access_token,
        page,
        sort,
        per_page,
      });
      const records = response?.records || [];
      const hydratedRecords = await hydratePollsForPosts(records);
      const hydratedResponse = {
        ...response,
        records: hydratedRecords,
      };
      dispatch(successHomePage(hydratedResponse));

      // Fetch badges, subscription status, and stripe connection for users in the feed
      if (hydratedRecords.length > 0) {
        const userIds = hydratedRecords.map(record => record?.author?.headline).filter(Boolean);

        if (userIds.length > 0) {
          dispatch(fetchUsersBadges(userIds));
          dispatch(fetchUsersSubscription(userIds));
          dispatch(fetchUsersStripeConnection(userIds));
        }
      }
      return hydratedResponse;
    })()
      .catch(error => {
        dispatch(errorHomePage(error));
        return { error: error.message || 'Failed to fetch home page' };
      })
      .finally(() => {
        homePageRequests.delete(requestKey);
      });

    homePageRequests.set(requestKey, request);
    return request;
  } catch (error) {
    dispatch(errorHomePage(error));
    return { error: error.message || 'Failed to fetch home page' };
  }
};

export const createSpacePost = (apiParams, spaceId, postId = null) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    const {
      bodyParam,
      notification,
      mentionedUserSgids,
      authorUserId,
      authorName,
      smsNotification,
      poll,
      clientRequestId,
    } = apiParams;

    dispatch(requestCreatePost());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const { thq_media_attachments, ...circleCreateBodyParam } = bodyParam || {};
    const params = {
      accessToken: access_token,
      spaceId,
      postId,
      data: postId ? bodyParam : circleCreateBodyParam,
      notification,
      mentionedUserSgids,
      authorUserId,
      authorName,
      smsNotification,
      ...(poll ? { poll } : {}),
      ...(clientRequestId ? { clientRequestId } : {}),
    };
    //by using admin api we can edit some attributes of post, like comment on/off
    let response = null;
    if (postId) {
      response = await editCirclePostApi(params);
    } else {
      response = await createSpacePostApi(params);
    }

    if (response?.success === false || response?.error) {
      throw response;
    }

    // Normalize and dispatch success immediately to reset loading state
    const normalizedResponse = response?.post?.id
      ? {
          ...response.post,
          ...(response.poll ? { poll: response.poll } : {}),
        }
      : response?.data?.id
      ? response.data
      : response;
    const normalizedPostEditBody = postId
      ? normalizePostRecord({ id: postId, ...(bodyParam || {}) })
      : {};
    const normalized = normalizePostRecord(
      postId
        ? {
            ...normalizedPostEditBody,
            ...(normalizedResponse || {}),
          }
        : normalizedResponse
    );
    dispatch(successCreatePost(normalized));

    // Run this asynchronously without blocking the response return
    // This will update the post again if there are attachments being transcoded
    if (
      !postId &&
      (normalized?.tiptap_body?.attachments?.length ||
        normalized?.tiptap_body?.inline_attachments?.length)
    ) {
      (async () => {
        try {
          const updatedResponse = await getMediaTranscodingStatusApi({
            postId: normalized.id,
            accessToken: access_token,
            spaceId: spaceId,
          });
          const normalizedUpdated = updatedResponse?.data?.id
            ? updatedResponse.data
            : updatedResponse;
          dispatch(successCreatePost(normalizedUpdated));
        } catch (error) {
          console.error('Media transcoding check failed:', error);
        }
      })();
    }
    return normalized;
  } catch (error) {
    console.error('Error creating/editing post:', error);
    dispatch(errorCreatePost(error));
    return null;
  }
};

export const submitPollVote = (pollId, selectedOptionIds = [], voterProfile = null) => async (
  dispatch,
  getState
) => {
  if (getState()?.CircleCommunityPage?.pollActionInProgressById?.[pollId]) {
    return null;
  }

  try {
    dispatch(requestPollAction(pollId, POLL_ACTION_VOTE));
    const response = await submitPollVoteApi(pollId, {
      selectedOptionIds,
      ...(voterProfile ? { voterProfile } : {}),
    });

    if (response?.success === false || !response?.poll) {
      throw response;
    }

    dispatch(successPollAction(pollId, response.poll));
    toastifyFn('success', 'Vote submitted');
    return response.poll;
  } catch (error) {
    const message = getPollActionErrorMessage(error);
    dispatch(errorPollAction(pollId, message));
    toastifyFn('error', message);
    return null;
  }
};

export const closePoll = pollId => async (dispatch, getState) => {
  if (getState()?.CircleCommunityPage?.pollActionInProgressById?.[pollId]) {
    return null;
  }

  try {
    dispatch(requestPollAction(pollId, POLL_ACTION_CLOSE));
    const response = await closePollApi(pollId);

    if (response?.success === false || !response?.poll) {
      throw response;
    }

    dispatch(successPollAction(pollId, response.poll));
    toastifyFn('success', 'Poll closed');
    return response.poll;
  } catch (error) {
    const message = getPollActionErrorMessage(error);
    dispatch(errorPollAction(pollId, message));
    toastifyFn('error', message);
    return null;
  }
};

export const createLikeDislikeForPost = (is_liked = false, postId, spaceId) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    const { requestLikePost } = getState().CircleCommunityPage;

    // Check lock FIRST before any async operations to prevent race conditions
    if (requestLikePost) {
      console.log('Like request blocked - already in progress');
      return;
    }
    dispatch(requestLikeDislikePost(postId));

    console.log('likeHit ..... ');

    const currentUser = getState()?.user?.currentUser;
    const likerUserId = currentUser?.id?.uuid;
    const likerName =
      currentUser?.attributes?.profile?.displayName ||
      `${currentUser?.attributes?.profile?.firstName || ''} ${currentUser?.attributes?.profile
        ?.lastName || ''}`.trim() ||
      'Someone';

    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    // Include additional data for notifications
    const params = {
      accessToken: access_token,
      postId,
      spaceId,
      likerUserId,
      likerName,
    };

    let response = null;
    if (!is_liked) {
      response = await createLikeForPostApi(params);
    } else {
      response = await createDislikeForPostApi(params);
    }
    if (response?.success) {
      dispatch(fetchSpecificPost({ spaceId, postId }));
    }
    dispatch(successLikeDislikePost(response));
    return response;
  } catch (error) {
    dispatch(errorLikeDislikePost(postId, error));
  }
};

export const createCommentForPost = (params, postId, notificationParams = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(requestCreateComment());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const { thq_media_attachments: localMediaAttachments = [], ...circleParams } = params || {};

    const response = await createCommentForPostApi({
      accessToken: access_token,
      postId,
      data: circleParams,
      // Include notification parameters
      ...notificationParams,
    });

    const mergedResponse = mergeLocalMediaIntoComment(response, localMediaAttachments);
    const hasMedia =
      Array.isArray(response?.tiptap_body?.attachments) &&
      response.tiptap_body.attachments.length > 0;
    const hasInlineMedia =
      Array.isArray(response?.tiptap_body?.inline_attachments) &&
      response.tiptap_body.inline_attachments.length > 0;

    // The comment is created successfully even if the optional transcoding
    // status endpoint is temporarily unavailable (for example, HTTP 503).
    // Update the feed immediately and refresh media metadata in the background.
    dispatch(successCreateComment(mergedResponse));

    if (hasMedia || hasInlineMedia) {
      getMediaTranscodingStatusApi({
        postId,
        commentID: response.id,
        accessToken: access_token,
      })
        .then(updatedResponse => {
          if (updatedResponse) {
            const mergedUpdatedResponse = mergeLocalMediaIntoComment(
              updatedResponse,
              localMediaAttachments
            );
            dispatch(successCreateComment(mergedUpdatedResponse));
          }
        })
        .catch(error => {
          console.error('Media transcoding check failed after comment creation:', error);
        });
    }

    return mergedResponse;
  } catch (error) {
    dispatch(errorCreateComment(error));
    // Return error object so the caller knows it failed
    return { error: error.message || 'Failed to create comment' };
  }
};

export const deleteCommentFromPost = (postId, commentID, spaceId, commenterUserId) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(requestDeleteComment());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await deleteCommentFromPostApi({
      accessToken: access_token,
      postId,
      commentID,
      spaceId,
      commenterUserId,
    });
    dispatch(successDeleteComment(response, commentID));
    return response;
  } catch (error) {
    dispatch(errorDeleteComment(error));
  }
};

export const deleteReplyFromComment = (parentCommentId, replyId, spaceId, replierUserId) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(requestDeleteComment()); // Reuse the same request action
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await deleteReplyFromCommentApi({
      accessToken: access_token,
      parentCommentId,
      replyId,
      spaceId,
      replierUserId,
    });
    dispatch(successDeleteComment(response, replyId)); // Reuse the same success action
    return response;
  } catch (error) {
    dispatch(errorDeleteComment(error));
  }
};

export const fetchSpaceList = (params = {}) => async (dispatch, getState, sdk) => {
  try {
    const { forceRefresh = false } = params || {};
    const { requestSpaceList, spaceListData } = getState().CircleCommunityPage || {};
    const { topbarGroups, topbarGroupsRequest } = getState().user || {};

    if (!forceRefresh && Array.isArray(spaceListData) && spaceListData.length > 0) {
      return spaceListData;
    }

    if (!forceRefresh && Array.isArray(topbarGroups) && topbarGroups.length > 0) {
      dispatch(successSpaceList(topbarGroups));
      // dispatch(fetchCombineSpaceTopics({}));
      return topbarGroups;
    }

    if (!forceRefresh && (requestSpaceList || topbarGroupsRequest)) {
      return spaceListData || topbarGroups || [];
    }

    dispatch(requestSpaceList());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await fetchSpaceListApi({ accessToken: access_token, forceRefresh });

    // const formattedResponse = groupBySpaceGroupName(response);
    dispatch(successSpaceList(response));
    if (response) {
      // dispatch(fetchCombineSpaceTopics({}));
    }
    return response;
  } catch (error) {
    dispatch(errorSpaceList(error));
  }
};
export const fetchSpaceListV2 = () => async (dispatch, getState, sdk) => {
  try {
    dispatch(requestSpaceListV2());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await fetchSpaceListApiV2({ accessToken: access_token });

    // const formattedResponse = groupBySpaceGroupName(response);
    dispatch(successSpaceListV2(response.records));
    // if (response) {
    //   dispatch(fetchCombineSpaceTopics({}));
    // }
    return response;
  } catch (error) {
    dispatch(errorSpaceListV2(error));
  }
};

export const createNewCircleUser = params => async (dispatch, getState, sdk) => {
  try {
    dispatch(requestCreateUser());
    const response = await createNewCircleUserApi(params);
    dispatch(successCreateUser(response));
    return response;
  } catch (error) {
    dispatch(errorCreateUser(error));
  }
};

const uploadWithMulter = async (file, direct_upload) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('direct_upload', JSON.stringify(direct_upload));

  const multipartResponse = await fetch(
    'http://localhost:3500/api/cloudfare/upload-file-with-multer',
    {
      method: 'POST',
      // headers: {
      //   'Content-Type': 'multipart/form-data',
      // },
      body: formData,
    }
  );
};

const uploadFileFromFrontend = async (file, direct_upload) => {
  try {
    const fileResponse = await fetch(direct_upload.url, {
      method: 'PUT',
      headers: direct_upload.headers,
      body: file,
    });

    if (!fileResponse.ok) {
      throw new Error(`Upload failed: ${fileResponse.status} ${fileResponse.statusText}`);
    }

    return fileResponse.status;
  } catch (error) {
    console.error('Error uploading file to storage:', error);
    throw new Error('Failed to upload file to storage');
  }
};

export const createDirectUpload = (params, file, options = {}) => async (dispatch, getState) => {
  console.log('\n--- [DIRECT UPLOAD] Starting ---');
  console.log('[DIRECT UPLOAD] File:', file.name, '| Size:', file.size, 'bytes');

  try {
    console.log('[DIRECT UPLOAD] Step 1: Dispatching requestDirectUpload action...');
    dispatch(requestDirectUpload());

    console.log('[DIRECT UPLOAD] Step 2: Getting user email and auth token...');
    const currentUser = getState()?.user?.currentUser;
    console.log('[DIRECT UPLOAD] User email:', currentUser?.attributes?.email);

    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    console.log('[DIRECT UPLOAD] ✓ Auth token obtained:', access_token ? 'YES' : 'NO');

    console.log('[DIRECT UPLOAD] Step 3: Calling directUploadApi...');
    console.log('[DIRECT UPLOAD] Request params:', JSON.stringify(params, null, 2));

    const response = await directUploadApi({
      accessToken: access_token,
      body: params,
      ...(options?.useAdminApi ? { useAdminApi: true } : {}),
    });

    console.log('[DIRECT UPLOAD] ✓ API response received:', JSON.stringify(response, null, 2));

    if (!response || !response.direct_upload) {
      console.error('[DIRECT UPLOAD] ✗ Invalid response structure:', response);
      throw new Error('Invalid response from direct upload API');
    }

    const { direct_upload } = response;
    console.log('[DIRECT UPLOAD] Step 4: Uploading file to S3...');
    console.log('[DIRECT UPLOAD] S3 URL:', direct_upload?.url);
    console.log('[DIRECT UPLOAD] S3 Headers:', direct_upload?.headers);

    const uploadStatus = await uploadFileFromFrontend(file, direct_upload);

    console.log('[DIRECT UPLOAD] ✓ S3 upload completed with status:', uploadStatus);

    // Check if upload was successful (status 200-299)
    if (uploadStatus < 200 || uploadStatus >= 300) {
      console.error('[DIRECT UPLOAD] ✗ Upload failed with status:', uploadStatus);
      throw new Error(`Upload failed with status: ${uploadStatus}`);
    }

    console.log('[DIRECT UPLOAD] Step 5: Dispatching success action...');
    dispatch(successDirectUpload({}));

    console.log('[DIRECT UPLOAD] ✓✓✓ Complete success! Returning response.');
    console.log('--- [DIRECT UPLOAD] End ---\n');
    return response;
  } catch (error) {
    console.error('\n!!! [DIRECT UPLOAD ERROR] !!!');
    console.error('[DIRECT UPLOAD] Error type:', error.constructor.name);
    console.error('[DIRECT UPLOAD] Error message:', error.message);
    console.error('[DIRECT UPLOAD] Error stack:', error.stack);
    console.error('[DIRECT UPLOAD] File that failed:', file.name);
    console.error('!!! [DIRECT UPLOAD ERROR END] !!!\n');

    dispatch(errorDirectUpload(error));
    // Re-throw the error so it propagates to the caller
    throw error;
  }
};

// export const createNewSpace = params => async (dispatch, getState, sdk) => {
//   try {
//     dispatch(requestNewSpace());
//     const { access_token } = await getCurrentUserCircleAuthToken(getState);

//     const response = await createNewSpaceApi({ accessToken: access_token, data: params });
//     dispatch(successNewSpace(response));
//     return response;
//   } catch (error) {
//     dispatch(errorNewSpace(error));
//   }
// };

export const joinSpace = params => async (dispatch, getState, sdk) => {
  try {
    dispatch(requestJoinSpace());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await joinSpaceByIdApi({ accessToken: access_token, spaceId: params });
    dispatch(getSpaceDetails({ spaceId: params }));
    dispatch(successJoinSpace(response));
    return response;
  } catch (error) {
    dispatch(errorJoinSpace(error));
  }
};

export const leaveSpace = params => async (dispatch, getState, sdk) => {
  try {
    dispatch(requestLeaveSpace());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await leaveSpaceByIdApi({ accessToken: access_token, spaceId: params });

    try {
      onGetModeratorCreateAndUpdateCollection({
        moderatorId: getState()?.user?.currentUser?.id?.uuid,
        groupId: params,
        groupName: 'delete',
        role: 'moderator',
        status: 'delete',
      });
    } catch (error) {}

    dispatch(getSpaceDetails({ spaceId: params }));
    dispatch(successLeaveSpace(response));
    return response;
  } catch (error) {
    dispatch(errorLeaveSpace(error));
  }
};

// export const fetchCombineSpaceTopics = params => async (dispatch, getState, sdk) => {
//   try {
//     dispatch(requestCombineSpaceTopics());
//     const { spaceListData } = getState().CircleCommunityPage;
//     const { access_token } = await getCurrentUserCircleAuthToken(getState);

//     const reponse = await trendingTopicsApi({
//       accessToken: access_token,
//       spaceListData: Array.isArray(spaceListData)
//         ? spaceListData.map(st => ({ is_private: st?.is_private, id: st.id, slug: st.slug }))
//         : [],
//     });
//     dispatch(successCombineSpaceTopics(reponse));
//     return reponse;
//   } catch (error) {}
// };

export const createBookMark = params => async (dispatch, getState, sdk) => {
  const { spaceId, postId, record_id, bookmark_type } = params;
  try {
    const { requestCreateBookmark: requestCreate } = getState().CircleCommunityPage;
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    if (requestCreate) return;
    dispatch(requestCreateBookmark(postId));
    const bodyParam = {
      record_id,
      bookmark_type,
    };
    const response = await createBookMarkApi({ accessToken: access_token, body: bodyParam });
    dispatch(fetchSpecificPost({ spaceId, postId }));
    dispatch(successCreateBookmark(response));
    return response;
  } catch (error) {
    dispatch(errorCreateBookmark(postId, error));
  }
};

export const deleteBookMark = params => async (dispatch, getState, sdk) => {
  const { spaceId, postId, bookmarkId } = params;
  try {
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const { requestDeleteBookmark: removeBookmark } = getState().CircleCommunityPage;

    if (removeBookmark) return;
    dispatch(requestDeleteBookmark(postId));
    const response = await deleteBookMarkApi({ accessToken: access_token, bookmarkId: bookmarkId });
    dispatch(fetchSpecificPost({ spaceId, postId }));
    dispatch(successDeleteBookmark(response));
    return response;
  } catch (error) {
    dispatch(errorDeleteBookmark(postId, error));
  }
};

export const fetchBookMarList = params => async (dispatch, getState, sdk) => {
  try {
    dispatch(requestBookmarksList());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await getBookMarksApi({ accessToken: access_token });
    dispatch(successBookmarksList(response));
    return response;
  } catch (error) {
    dispatch(errorBookmarksList(error));
  }
};

export const fetchSpecificPost = params => async (dispatch, getState) => {
  try {
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    dispatch(requestSinglePost());
    const response = await getSinglePostApi({
      accessToken: access_token,
      spaceId: params.spaceId,
      postId: params.postId,
    });
    const hydratedResponse = await hydratePollForPost(response);
    dispatch(successSinglePost(hydratedResponse));

    // Fetch author's badges, subscription status, and stripe connection if headline (userId) is available
    const authorUserId = hydratedResponse?.author?.headline;
    if (authorUserId) {
      dispatch(fetchUsersBadges([authorUserId]));
      dispatch(fetchUsersSubscription([authorUserId]));
      dispatch(fetchUsersStripeConnection([authorUserId]));
    }

    if (hydratedResponse?.tiptap_body?.attachments.length) {
      (async () => {
        try {
          const updatedResponse = await getMediaTranscodingStatusApi({
            postId: hydratedResponse.id,
            accessToken: access_token,
            spaceId: params.spaceId,
          });
          const hydratedUpdatedResponse = await hydratePollForPost(updatedResponse);
          dispatch(successSinglePost(hydratedUpdatedResponse));
        } catch (error) {
          console.error('Media transcoding check failed:', error);
        }
      })();
    }

    return hydratedResponse;
  } catch (error) {
    dispatch(errorSinglePost(error));
  }
};

export const fetchPostCommentList = params => async (dispatch, getState) => {
  try {
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const { page = 1, per_page = 2, postId = '' } = params;
    dispatch(requestPostCommentList(page));
    const response = await getCommentListForPostApi({
      accessToken: access_token,
      ...params,
      // postId: params.postId,
    });
    dispatch(successPostCommentList(response, postId));

    // Fetch badges for comment authors
    const comments = response?.records || [];
    const authorUserIds = comments.map(comment => comment?.author?.headline).filter(Boolean);

    // Also get reply authors if any
    comments.forEach(comment => {
      if (comment?.replies && Array.isArray(comment.replies)) {
        comment.replies.forEach(reply => {
          if (reply?.author?.headline) {
            authorUserIds.push(reply.author.headline);
          }
        });
      }
    });

    if (authorUserIds.length > 0) {
      const uniqueUserIds = [...new Set(authorUserIds)];
      dispatch(fetchUsersBadges(uniqueUserIds)); // Remove duplicates
      dispatch(fetchUsersSubscription(uniqueUserIds)); // Fetch subscription status for comment authors
      dispatch(fetchUsersStripeConnection(uniqueUserIds)); // Fetch stripe connection status for comment authors
    }

    return response;
  } catch (error) {
    dispatch(errorPostCommentList(error));
  }
};

export const createLikeDislikeComment = (params, notificationParams = {}) => async (
  dispatch,
  getState
) => {
  try {
    console.log(notificationParams, ' checking likerUserId notification params .... ');
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    dispatch(requestLikeDislikeComment(params.commentId));
    const response = await createLikeDislikeCommentApi({
      accessToken: access_token,
      commentId: params.commentId,
      isLiked: params.is_liked,
      // Include notification parameters
      ...notificationParams,
    });
    dispatch(successLikeDislikeComment(response));
    return response;
  } catch (error) {
    dispatch(errorLikeDislikeComment(params.commentId, error));
  }
};

export const createReplyComment = params => async (dispatch, getState) => {
  try {
    // Prevent duplicate replies when the submit event is triggered more than
    // once before the first request has completed.
    if (getState()?.CircleCommunityPage?.requestCreateReplyComment) {
      return null;
    }

    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const {
      thq_media_attachments: localMediaAttachments = [],
      ...circleBodyParams
    } = params?.bodyParams || {};

    dispatch(requestCommentReply());
    console.log('Hit Reply on comment ...... ');
    const response = await createReplyForACommentApi({
      accessToken: access_token,
      data: circleBodyParams,
      commentId: params.commentId,
      // Pass notification parameters if available
      ...(params.notificationParams && {
        postId: params.notificationParams.postId,
        spaceId: params.notificationParams.spaceId,
        replierUserId: params.notificationParams.replierUserId,
        replierName: params.notificationParams.replierName,
        mentionedUserSgids: params.notificationParams.mentionedUserSgids,
      }),
    });
    const mergedResponse = mergeLocalMediaIntoComment(response, localMediaAttachments);
    dispatch(successCommentReply(mergedResponse));
    return mergedResponse; // Return the response
  } catch (error) {
    dispatch(errorCommentReply(error));
    // Return error object so the caller knows it failed
    return { error: error.message || 'Failed to create reply' };
  }
};

export const getSpaceDetails = params => async (dispatch, getState) => {
  try {
    const { page = 1, sort, per_page = 10 } = params;
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    dispatch(fetchCommunityMember());
    dispatch(requestSpaceDetails(page));
    const response = await getSpaceDetailsApi({
      accessToken: access_token,
      spaceId: params.spaceId,
      page,
      sort,
      per_page,
      topicId: params?.topicId,
      forceRefresh: params?.forceRefresh,
    });
    const posts = response?.postResponse?.records || [];
    const hydratedPosts = await hydratePollsForPosts(posts);
    const hydratedResponse = {
      ...response,
      postResponse: {
        ...(response?.postResponse || {}),
        records: hydratedPosts,
      },
    };
    dispatch(getSpaceMembersList({ space_id: params.spaceId }, false));
    dispatch(successSpaceDetails(hydratedResponse));

    // Fetch badges, subscription status, and stripe connection for all post authors
    const authorUserIds = hydratedPosts.map(post => post?.author?.headline).filter(Boolean);
    if (authorUserIds.length > 0) {
      dispatch(fetchUsersBadges(authorUserIds));
      dispatch(fetchUsersSubscription(authorUserIds));
      dispatch(fetchUsersStripeConnection(authorUserIds));
    }

    return hydratedResponse;
  } catch (error) {
    dispatch(errorSpaceDetails(error));
  }
};

export const getSpaceTopicDetails = params => async (dispatch, getState) => {
  try {
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    dispatch(requestTopicDetails());
    const response = await getSpaceTopicDetailApi({
      accessToken: access_token,
      spaceId: params.spaceId,
      topicId: params.topicId,
    });
    dispatch(successTopicDetails(response));
    return response;
  } catch (error) {
    dispatch(errorTopicDetails(error));
  }
};

export const getSpaceMembersList = (params, isShowFullList = false, isAdmin = false) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(requestSpaceMembersList());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await getSpaceMembersApi({ accessToken: access_token, data: params, isAdmin });
    if (isShowFullList) {
      dispatch(successSpaceMemberFullList(response));
      return response;
    } else {
      dispatch(successSpaceMembersList(response));
      return response;
    }
  } catch (error) {
    dispatch(errorSpaceMembersList(error));
  }
};

export const requestToJoinGroupNotify = params => async (dispatch, getState) => {
  try {
    const results = await onGetModeratorFindCollection({
      payload: {
        groupId: params.data.groupId,
        status: 'active',
        role: 'moderator',
      },
      pageParams: {
        page: 1,
        perPage: 10,
      },
    });

    let allEmails = [];

    allEmails = results.data.filter(st => st?.userEmail).map(item => ({ email: item?.userEmail }));

    // if no modeartor is present in group the fetch admin
    if (!allEmails.length) {
      const adminResults = await onGetModeratorFindCollection({
        payload: {
          groupId: params.data.groupId,
          status: 'active',
          role: 'admin',
        },
        pageParams: {
          page: 1,
          perPage: 10,
        },
      });
      allEmails = adminResults.data
        .filter(st => st?.userEmail)
        .map(item => ({ email: item?.userEmail }));
    }

    const validEmails =
      Array.isArray(allEmails) && allEmails.length > 0
        ? allEmails
        : [{ email: process.env.REACT_APP_SENDGRID_ADMIN_RECIVER_EMAIL }];

    Object.assign(params.data, {
      personalizationsTo: validEmails.filter((_, i) => i < 10),
    });
    return onMultiMailApiCollection(params);
  } catch (error) {
    return error;
  }
};

export const uploadDocument = params => (dispatch, getState) => {
  dispatch(requestUploadDocument());
  return UploadImage({ ...params, folder: 'group-documents' })
    .then(response => {
      dispatch(successUploadDocument(response));
      return response;
    })
    .catch(error => {
      dispatch(errorUploadDocument(storableError(error)));
    });
};

export const removeSpaceMember = params => async (dispatch, getState) => {
  try {
    dispatch(requestRemoveSpaceMember());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await removeSpaceMemberApi({
      accessToken: access_token,
      email: params.email,
      space_id: params.spaceId,
      community_member_id: params.community_member_id,
    });

    dispatch(successRemoveSpaceMember({}, params?.community_member_id));
    return response;
  } catch (error) {
    dispatch(errorRemoveSpaceMember(error));
    throw error;
  }
};

export const deleteSpace = params => async (dispatch, getState) => {
  try {
    dispatch(requestDeleteSpace());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await deleteSpaceApi({ accessToken: access_token, space_id: params });
    dispatch(getSpaceDetails({ spaceId: params }));
    dispatch(successDeleteSpace(response));
    return response;
  } catch (error) {
    dispatch(errorDeleteSpace(error));
  }
};

export const advancedSearch = params => async (dispatch, getState) => {
  try {
    dispatch(requestAdvancedSearch());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await advancedSearchApi({ accessToken: access_token, ...params });
    dispatch(successAdvancedSearch(response));
    return response;
  } catch (error) {
    dispatch(errorAdvancedSearch(error));
  }
};

export const editCommentForPost = (params, postId, commentID) => async (dispatch, getState) => {
  try {
    dispatch(requestEditComment());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const { thq_media_attachments: localMediaAttachments = [], ...circleParams } = params || {};

    const response = await editCommentApi({
      accessToken: access_token,
      postId,
      commentID,
      data: circleParams,
    });

    const mergedResponse = mergeLocalMediaIntoComment(response, localMediaAttachments);
    dispatch(successEditComment(mergedResponse));
    return mergedResponse;
  } catch (error) {
    dispatch(errorEditComment(error));
  }
};

export const editReplyComment = (params, commentId, replyId) => async (dispatch, getState) => {
  try {
    dispatch(requestEditComment());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const { thq_media_attachments: localMediaAttachments = [], ...circleParams } = params || {};

    const response = await editReplyCommentApi({
      accessToken: access_token,
      commentId,
      replyId,
      data: circleParams,
    });

    const mergedResponse = mergeLocalMediaIntoComment(response, localMediaAttachments);
    dispatch(successEditComment(mergedResponse));
    return mergedResponse;
  } catch (error) {
    dispatch(errorEditComment(error));
  }
};

export const deleteCirclePost = (postId, spaceId) => async (dispatch, getState) => {
  try {
    dispatch(requestDeletePost(postId));
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await deleteCirclePostApi({ accessToken: access_token, postId, spaceId });
    if (response?.success) {
      dispatch(successDeletePost(postId));
      toastifyFn('success', 'Post was deleted');
    }
    return response;
  } catch (error) {
    dispatch(errorDeletePost(error));
    toastifyFn('error', 'Could not delete post');
  }
};

export const followUnfollowPost = (postId, spaceId, post_follower_id) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(requestFollowPost(postId));
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await followCirclePostApi({
      accessToken: access_token,
      postId,
      spaceId,
      post_follower_id,
    });

    dispatch(successFollowPost(response));

    return response;
  } catch (error) {
    dispatch(errorFollowPost(error));
  }
};

export const flaggedContent = params => async (dispatch, getState) => {
  try {
    dispatch(requestFlaggedContent());
    const { access_token } = await getCurrentUserCircleAuthToken(getState); // accessToken: access_token,

    const response = await flaggedContentApi({ accessToken: access_token, data: params });
    dispatch(successFlaggedContent(response));
    dispatch(toggleReportContent());
    return response;
  } catch (error) {
    dispatch(errorFlaggedContent(error));
  }
};

export const getSpaceTopics = params => async (dispatch, getState) => {
  try {
    dispatch(requestSpaceTopics());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await getSpaceTopicsApi({ accessToken: access_token, spaceId: params });
    dispatch(successSpaceTopics(response));
    return response;
  } catch (error) {
    dispatch(errorSpaceTopics(error));
  }
};

export const getNotificationsList = params => async (dispatch, getState) => {
  const { forceFresh = false } = params || {};

  if (notificationsListRequest && !forceFresh) {
    return notificationsListRequest;
  }

  const requestId = ++notificationsListRequestId;

  try {
    dispatch(requestNotifications({ clearData: forceFresh }));

    notificationsListRequest = (async () => {
      const userId = getState()?.user?.currentUser?.id?.uuid;
      const { access_token } = await getCurrentUserCircleAuthToken(getState);
      return getNotificationsListApi({
        accessToken: access_token,
        userId,
        ...(forceFresh ? { cacheBust: Date.now() } : {}),
      });
    })();

    const response = await notificationsListRequest;
    if (requestId === notificationsListRequestId) {
      dispatch(successNotifications(response));
    }
    return response;
  } catch (error) {
    console.log('error', error);
    if (requestId === notificationsListRequestId) {
      dispatch(errorNotifications(error));
    }
  } finally {
    if (requestId === notificationsListRequestId) {
      notificationsListRequest = null;
    }
  }
};

export const getNotificationsCount = params => async (dispatch, getState) => {
  const { forceFresh = false } = params || {};

  if (notificationsCountRequest && !forceFresh) {
    return notificationsCountRequest;
  }

  try {
    dispatch(requestNotificationCount());

    notificationsCountRequest = (async () => {
      const userId = getState()?.user?.currentUser?.id?.uuid;
      const { access_token } = await getCurrentUserCircleAuthToken(getState);
      return access_token
        ? getNotificationsCountApi({
            accessToken: access_token,
            userId,
            ...(forceFresh ? { cacheBust: Date.now(), forceFresh: true } : {}),
          })
        : null;
    })();

    const response = await notificationsCountRequest;
    dispatch(successNotificationCount(response));
    return response;
  } catch (error) {
    console.log('error', error);
    dispatch(errorNotificationCount(error));
  } finally {
    notificationsCountRequest = null;
  }
};

export const markNotificationAsRead = params => async (dispatch, getState) => {
  try {
    dispatch(requestMarkAsRead());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await markNotificationAsReadApi({ ...params, accessToken: access_token });
    dispatch(successMarkAsRead(response));
    return response;
  } catch (error) {
    console.log('error', error);
    dispatch(errorMarkAsRead(error));
  }
};
export const resetNotificationCount = params => async (dispatch, getState) => {
  try {
    dispatch(requestResetNotifications());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const userId = getState()?.user?.currentUser?.id?.uuid;
    const response = await resetNotificationCountApi({ accessToken: access_token, userId });

    dispatch(successResetNotifications(response));
    return response;
  } catch (error) {
    console.log('error', error);
    dispatch(errorResetNotifications(error));
  }
};

export const getMemberDetail = params => async (dispatch, getState) => {
  try {
    dispatch(requestMemberDetail());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await getMemberDetailApi({
      accessToken: access_token,
      community_member_id: params,
    });
    dispatch(successMemberDetail(response));
    return response;
  } catch (error) {
    console.log('error', error);
    dispatch(errorMemberDetail(error));
  }
};

export const loadMoreReplyComments = params => async (dispatch, getState) => {
  try {
    dispatch(requestCommentReplyList());
    const { page, per_page, commentId } = params;
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await getListOfReplyForCommentApi({ accessToken: access_token, ...params });

    dispatch(successCommentReplyList(response, commentId));
    return response;
  } catch (error) {
    dispatch(errorCommentReplyList(error));
  }
};

export const markAllNotificationsAsRead = () => async (dispatch, getState) => {
  try {
    dispatch(requestMarkAllNotification());
    const userId = getState()?.user?.currentUser?.id?.uuid;
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const resposne = await markAllNotificationsAsReadApi({ accessToken: access_token, userId });
    dispatch(getNotificationsList());
    dispatch(successMarkAllNotification(resposne));
    return resposne;
  } catch (error) {
    dispatch(errorMarkAllNotification(error));
  }
};

export const getNotificationPreference = () => async (dispatch, getState) => {
  try {
    dispatch(requestNotificationPreference());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const resposne = await getNotificationPrefernceApi({ accessToken: access_token });
    dispatch(successNotificationPreference(resposne));
    return resposne;
  } catch (error) {
    dispatch(errorNotificationPreference(error));
  }
};

export const updateNotificationPreference = params => async (dispatch, getState) => {
  try {
    dispatch(requestNotificationPreference());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const resposne = await notificationPreferencesApi({ accessToken: access_token, ...params });
    dispatch(getNotificationPreference());
    // dispatch(successNotificationPreference(resposne))
    return resposne;
  } catch (error) {
    dispatch(errorNotificationPreference(error));
  }
};

export const fetchUserActivity = params => async (dispatch, getState) => {
  try {
    dispatch(fetchUserActivityRequest());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await fetchUserActivityApi({ accessToken: access_token, ...params });
    dispatch(fetchUserActivitySuccess(response));
  } catch (e) {
    console.log(e, '%%% %%% => e');
    dispatch(fetchUserActivityError(e));
  }
};

export const fetchGroupHighlightsStatus = spaceId => async dispatch => {
  try {
    if (!spaceId) return;
    const response = await getGroupHighlightsApi({
      spaceId,
      statusOnly: true,
    });
    dispatch(successGroupHighlightsStatus(response));
    return response;
  } catch (error) {
    // Status fetch is non-blocking; red dot falls back to no unseen highlights
    console.error('Failed to fetch highlight status:', error);
  }
};

export const fetchGroupHighlights = spaceId => async (dispatch, getState) => {
  try {
    if (!spaceId) return;
    dispatch(requestGroupHighlights());
    const { access_token = null } = await getCurrentUserCircleAuthToken(getState);
    const response = await getGroupHighlightsApi({
      accessToken: access_token,
      spaceId,
    });
    const hydratedResponse = response?.records
      ? {
          ...response,
          records: await hydratePollsForPosts(response.records),
        }
      : response;
    dispatch(successGroupHighlights(hydratedResponse));
    return hydratedResponse;
  } catch (error) {
    dispatch(errorGroupHighlights(error));
  }
};

export const pinPostToHighlights = (postId, spaceId) => async (dispatch, getState) => {
  try {
    dispatch(requestPinGroupHighlight());
    const { currentUser } = getState().user || {};
    const userId = currentUser?.id?.uuid;
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await pinGroupHighlightApi({
      accessToken: access_token,
      spaceId,
      postId,
      userId,
    });
    if (response?.success) {
      dispatch(successPinGroupHighlight(response.post, response.postId));
    } else {
      dispatch(errorPinGroupHighlight(response));
    }
    return response;
  } catch (error) {
    dispatch(errorPinGroupHighlight(error));
  }
};

export const unpinPostFromHighlights = (postId, spaceId) => async (dispatch, getState) => {
  try {
    dispatch(requestPinGroupHighlight());
    const { currentUser } = getState().user || {};
    const userId = currentUser?.id?.uuid;
    const response = await unpinGroupHighlightApi({
      spaceId,
      postId,
      userId,
    });
    if (response?.success) {
      dispatch(successUnpinGroupHighlight(response.postId));
    } else {
      dispatch(errorPinGroupHighlight(response));
    }
    return response;
  } catch (error) {
    dispatch(errorPinGroupHighlight(error));
  }
};

export const addMemberToSpace = params => async (dispatch, getState) => {
  try {
    dispatch(requestAddMember());
    const { access_token } = await getCurrentUserCircleAuthToken(getState);

    const response = await addMemberToSpaceApi({ accessToken: access_token, ...params });

    dispatch(successAddMember(response));
    return response;
  } catch (error) {
    dispatch(errorAddMember(error));
    throw error;
  }
};

export const groupPostSearch = params => async (dispatch, getState) => {
  try {
    const { page = 1 } = params;
    dispatch(requestGroupPostSearch(page));
    const { access_token } = await getCurrentUserCircleAuthToken(getState);
    const response = await groupPostSearchApi({ accessToken: access_token, ...params });
    dispatch(successGroupPostSearch(response));
    return response;
  } catch (error) {
    dispatch(errorGroupPostSearch(error));
  }
};

// Badge action creators
export const fetchUsersBadgesRequest = () => ({
  type: FETCH_USERS_BADGES_REQUEST,
});

export const fetchUsersBadgesSuccess = userBadgesMap => ({
  type: FETCH_USERS_BADGES_SUCCESS,
  payload: userBadgesMap,
});

export const fetchUsersBadgesError = error => ({
  type: FETCH_USERS_BADGES_ERROR,
  payload: error,
});

// Thunk to fetch badges for multiple users
export const fetchUsersBadges = userIds => async (dispatch, getState) => {
  const normalizedUserIds = Array.isArray(userIds)
    ? [...new Set(userIds.map(id => (id == null ? '' : String(id).trim())).filter(Boolean))]
    : [];

  if (normalizedUserIds.length === 0) return;

  // Filter out userIds we already have badges for, plus ids already being requested.
  const existingBadgesMap = getState().CircleCommunityPage.usersBadgesMap || {};
  const newUserIds = normalizedUserIds.filter(
    id => !existingBadgesMap[id] && !usersBadgesRequests.has(id)
  );

  if (newUserIds.length === 0) return;

  try {
    newUserIds.forEach(id => usersBadgesRequests.add(id));
    dispatch(fetchUsersBadgesRequest());
    const response = await fetchUsersWithBadgesApi({ userIds: newUserIds });
    dispatch(fetchUsersBadgesSuccess(response.userBadgesMap || {}));
    return response;
  } catch (error) {
    console.error('Error fetching users badges:', error);
    dispatch(fetchUsersBadgesError(error.message));
  } finally {
    newUserIds.forEach(id => usersBadgesRequests.delete(id));
  }
};

// Subscription status action creators
export const fetchUsersSubscriptionRequest = () => ({
  type: FETCH_USERS_SUBSCRIPTION_REQUEST,
});

export const fetchUsersSubscriptionSuccess = userSubscriptionMap => ({
  type: FETCH_USERS_SUBSCRIPTION_SUCCESS,
  payload: userSubscriptionMap,
});

export const fetchUsersSubscriptionError = error => ({
  type: FETCH_USERS_SUBSCRIPTION_ERROR,
  payload: error,
});

// Thunk to fetch subscription status for multiple users
export const fetchUsersSubscription = userIds => async (dispatch, getState) => {
  if (!userIds || userIds.length === 0) return;

  // Filter out userIds we already have, plus ids already being requested.
  const existingSubscriptionMap = getState().CircleCommunityPage.usersSubscriptionMap || {};
  const newUserIds = [...new Set(userIds)].filter(
    id => existingSubscriptionMap[id] === undefined && !usersSubscriptionRequests.has(id)
  );

  if (newUserIds.length === 0) return;

  try {
    newUserIds.forEach(id => usersSubscriptionRequests.add(id));
    dispatch(fetchUsersSubscriptionRequest());
    const response = await fetchUsersSubscriptionStatusApi({ userIds: newUserIds });
    dispatch(fetchUsersSubscriptionSuccess(response.userSubscriptionMap || {}));
    return response;
  } catch (error) {
    console.error('Error fetching users subscription status:', error);
    dispatch(fetchUsersSubscriptionError(error.message));
  } finally {
    newUserIds.forEach(id => usersSubscriptionRequests.delete(id));
  }
};

// Stripe connection action creators
export const fetchUsersStripeConnectionRequest = () => ({
  type: FETCH_USERS_STRIPE_CONNECTION_REQUEST,
});

export const fetchUsersStripeConnectionSuccess = userStripeConnectionMap => ({
  type: FETCH_USERS_STRIPE_CONNECTION_SUCCESS,
  payload: userStripeConnectionMap,
});

export const fetchUsersStripeConnectionError = error => ({
  type: FETCH_USERS_STRIPE_CONNECTION_ERROR,
  payload: error,
});

// Thunk to fetch stripe connection status for multiple users
export const fetchUsersStripeConnection = userIds => async (dispatch, getState) => {
  if (!userIds || userIds.length === 0) return;

  // Filter out userIds we already have, plus ids already being requested.
  const existingStripeConnectionMap = getState().CircleCommunityPage.usersStripeConnectionMap || {};
  const newUserIds = [...new Set(userIds)].filter(
    id => existingStripeConnectionMap[id] === undefined && !usersStripeConnectionRequests.has(id)
  );

  if (newUserIds.length === 0) return;

  try {
    newUserIds.forEach(id => usersStripeConnectionRequests.add(id));
    dispatch(fetchUsersStripeConnectionRequest());
    const response = await getUsersStripeConnectionApi({ userIds: newUserIds });
    dispatch(fetchUsersStripeConnectionSuccess(response.userStripeConnectionMap || {}));
    return response;
  } catch (error) {
    console.error('Error fetching users stripe connection status:', error);
    dispatch(fetchUsersStripeConnectionError(error.message));
  } finally {
    newUserIds.forEach(id => usersStripeConnectionRequests.delete(id));
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const { page = 1 } = parse(search); // Parse the 'page' number from the search string, defaulting to 1
  const { postId, spaceId } = params; // Extract route parameters
  dispatch(requestHomePage(page));
  // Fetch the current user
  return Promise.all([dispatch(fetchCurrentUser())])
    .then(async response => {
      // Get the current user's data from state
      const currentUser = getState().user.currentUser;
      const email = currentUser?.attributes?.email;
      const currentUserId = currentUser?.id?.uuid;

      if (email) {
        // fetch auth token on first load
        await setInitalLoadToken(getCurrentUserCircleAuthParams(currentUser));
        // Fetch Circle auth token with pagination
        const loadPromises = [
          dispatch(fetchCircleAuthToken({ page: +page, forceRefreshSpaceList: true })),
        ];

        // Fetch current user's subscription status and stripe connection for showing badge on comment input
        if (currentUserId) {
          loadPromises.push(dispatch(fetchUsersSubscription([currentUserId])));
          loadPromises.push(dispatch(fetchUsersStripeConnection([currentUserId])));
        }

        // If both postId and spaceId are available, fetch post and space members
        if (postId && spaceId) {
          loadPromises.push(dispatch(fetchSpecificPost({ postId, spaceId }))); // Fetch the specific post
          loadPromises.push(dispatch(getSpaceMembersList({ space_id: spaceId }, false))); // Fetch members of the space
        }

        await Promise.all(loadPromises);
      }
      return response;
    })
    .catch(e => {
      throw e;
    });
};
